import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  Checkbox,
  ListItemText,
  Grid
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import Select from '../components/SearchableSelect';
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteTenantLogo,
  getTenantSettings,
  updateTenantSettings,
  uploadTenantLogo,
} from "../APIs/TenantSettingsAPI";
import { getConfiguredPrinters } from "../APIs/PrintersAPI";
import {
  deleteHeliteServiceMapping,
  getHeliteChargeCodes,
  getHeliteServiceMappings,
  saveHeliteServiceMapping,
} from "../APIs/HeliteAPI";
import { getAllProdotti } from "../APIs/ProductsAPI";
import { getAllCategorie } from "../APIs/CategoriesAPI";
import { getAllSottocategorie } from "../APIs/SubCategoriesAPI";
import { getAllStanze } from "../APIs/RoomsAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import TimePicker24Field from "../components/TimePicker24Field";
import { italianErrorMessage } from "../utils/italianErrorMessage";

const BACKEND_TABLE_VIEW_TO_UI = {
  STANDARD: "standard",
  DETAILED: "dettagliata",
};

const UI_TABLE_VIEW_TO_BACKEND = {
  standard: "STANDARD",
  dettagliata: "DETAILED",
};

const ORDER_PRINT_FONT_SIZES = ['SMALL', 'NORMAL', 'LARGE'];
const normalizeOrderPrintFontSize = (value) => (
  ORDER_PRINT_FONT_SIZES.includes(value) ? value : 'NORMAL'
);

const DEFAULT_HELITE_SETTINGS = {
  heliteUrl: "https://helitepms.sigesgroup.it/NuovoPMSTest",
  heliteClientId: "pwNv47EbdGmvtTCHklUcEk0eEH1eNn7RqgeLust3",
  heliteUsername: "he_risto",
  helitePassword: "he_risto",
  heliteHotelCode: "32189_1",
  heliteStructCode: "",
  heliteDepCode: "",
  heliteServiceCode: "",
  hasHelitePassword: false,
};

const HELITE_MAPPING_SCOPE_LABELS = {
  DEFAULT: "Vendita default",
  CATEGORY: "Categoria",
  SUBCATEGORY: "Sottocategoria",
  PRODUCT: "Prodotto",
};

const timeToMinutes = (value) => {
  if (!value || typeof value !== "string") return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const padTimePart = (value) => String(value).padStart(2, "0");

const parseTimePickerValue = (value) => {
  if (!value || typeof value !== "string") return null;
  const [hours, minutes] = value.split(":").map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const formatTimePickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}`;
};

const hasSavedHeliteSettings = (settings) =>
  Boolean(
    settings?.heliteUrl ||
    settings?.heliteClientId ||
    settings?.heliteUsername ||
    settings?.heliteHotelCode ||
    settings?.heliteStructCode ||
    settings?.heliteDepCode ||
    settings?.heliteServiceCode ||
    settings?.hasHelitePassword
  );

const mapHeliteSettingsToUi = (settings) => {
  if (!hasSavedHeliteSettings(settings)) {
    return { ...DEFAULT_HELITE_SETTINGS };
  }

  return {
    heliteUrl: settings?.heliteUrl || "",
    heliteClientId: settings?.heliteClientId || "",
    heliteUsername: settings?.heliteUsername || "",
    helitePassword: "",
    heliteHotelCode: settings?.heliteHotelCode || "",
    heliteStructCode: settings?.heliteStructCode || "",
    heliteDepCode: settings?.heliteDepCode || "",
    heliteServiceCode: settings?.heliteServiceCode || "",
    hasHelitePassword: Boolean(settings?.hasHelitePassword),
  };
};

const normalizeVatPercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const vatPercent = Number(String(value).replace(",", "."));
  if (!Number.isFinite(vatPercent)) return null;
  return Math.round(vatPercent * 10000) / 10000;
};

const ratesMatch = (a, b) => {
  const first = normalizeVatPercent(a);
  const second = normalizeVatPercent(b);
  return first !== null && second !== null && Math.abs(first - second) < 0.0001;
};

const formatVatPercent = (value) => {
  const vatPercent = normalizeVatPercent(value);
  if (vatPercent === null) return "-";
  return Number.isInteger(vatPercent) ? String(vatPercent) : String(vatPercent).replace(".", ",");
};

const mappingTargetKey = (target) => [
  target.scopeType || target.targetType,
  target.scopeId ?? target.targetId ?? "default",
  formatVatPercent(target.vatPercent ?? target.vatRate),
].join(":");

const debitKey = (structureCode, debit) => [
  structureCode,
  debit?.dep || "",
  debit?.service || "",
].join(":");

const ruleDebitKey = (rule) => debitKey(rule?.structCode, {
  dep: rule?.dep,
  service: rule?.service,
});

const mappingRuleKey = (rule) => [
  mappingTargetKey(rule),
  ruleDebitKey(rule),
].join("::");

const normalizeIdArray = (values) => [...new Set((Array.isArray(values) ? values : [])
  .map(Number)
  .filter((value) => Number.isInteger(value) && value > 0))]
  .sort((a, b) => a - b);

const normalizeNameArray = (values) => [...new Set((Array.isArray(values) ? values : [])
  .map((value) => String(value || "").trim())
  .filter(Boolean))]
  .sort((a, b) => a.localeCompare(b));

const arraysEqual = (first, second) => {
  if (first.length !== second.length) return false;
  return first.every((value, index) => value === second[index]);
};

function SystemSettingsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const logoInputRef = useRef(null);
  const showSnackbarRef = useRef(showSnackbar);
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = localStorage.getItem("tenantType");
  const isConferenceTenant = String(tenantType || "").toUpperCase() === "SALE_CONGRESSI";
  const tenantTypeLabel = t(`systemSettings.tenantTypes.${String(tenantType || "").toUpperCase()}`, {
    defaultValue: String(tenantType || "-").replace(/_/g, " ").toLowerCase(),
  });
  const accentColor = theme.palette.warning.main;
  const cardBorder = alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.75 : 0.95);
  const sectionCardSx = {
    border: `1px solid ${cardBorder}`,
    borderRadius: 2,
    overflow: "hidden",
    maxWidth: "100%",
    minWidth: 0,
    background: theme.palette.background.paper,
    boxShadow: theme.palette.mode === "dark"
      ? "none"
      : `0 18px 42px ${alpha(theme.palette.common.black, 0.08)}`,
  };
  const sectionHeaderSx = {
    px: { xs: 2, md: 2.5 },
    py: 2,
    borderBottom: `1px solid ${cardBorder}`,
    background: alpha(accentColor, theme.palette.mode === "dark" ? 0.12 : 0.08),
  };

  const [activeSection, setActiveSection] = useState("general");
  const [showOrderImages, setShowOrderImages] = useState(true);
  const [showFixedMenuNameOnKitchenPrint, setShowFixedMenuNameOnKitchenPrint] = useState(false);
  const [orderPrintFontSize, setOrderPrintFontSize] = useState('NORMAL');
  const [defaultTableMapView, setDefaultTableMapView] = useState("standard");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [configuredPrinters, setConfiguredPrinters] = useState([]);
  const [draftPrinterTargets, setDraftPrinterTargets] = useState([]);
  const [heliteBuonoPrinterTargets, setHeliteBuonoPrinterTargets] = useState([]);
  const [voucherPrinterTargets, setVoucherPrinterTargets] = useState([]);
  const [invoiceCourtesyPrinterTargets, setInvoiceCourtesyPrinterTargets] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [eventConfirmedColor, setEventConfirmedColor] = useState("#2E7D32");
  const [eventInProgressColor, setEventInProgressColor] = useState("#F59E0B");
  const [eventCompletedColor, setEventCompletedColor] = useState("#16A34A");
  const [quoteExpiredColor, setQuoteExpiredColor] = useState("#DC2626");
  const [defaultVatRate, setDefaultVatRate] = useState("10");
  const [tenantLogo, setTenantLogo] = useState({
    base64: "",
    fileName: "",
  });
  const [tenantLogoFile, setTenantLogoFile] = useState(null);

  const [billingData, setBillingData] = useState({
    ownerName: "",
    ownerSurname: "",
    ownerTaxCode: "",
    ownerVat: "",
    ownerAddress: "",
    ownerPostalCode: "",
    ownerCity: "",
    ownerProvince: "",
    ownerEmail: "",
    ownerPhone: "",
  });

  const [easyInvoiceLogin, setEasyInvoiceLogin] = useState({
    user: "",
    client_id: "",
    secret: "",
  })

  const [heliteData, setHeliteData] = useState({ ...DEFAULT_HELITE_SETTINGS });
  const [heliteSettingsSaved, setHeliteSettingsSaved] = useState(false);
  const [heliteChargeCodes, setHeliteChargeCodes] = useState([]);
  const [heliteChargeCodesLoading, setHeliteChargeCodesLoading] = useState(false);
  const [heliteMappingRules, setHeliteMappingRules] = useState([]);
  const [savedHeliteMappingRules, setSavedHeliteMappingRules] = useState([]);
  const [heliteMappingCatalog, setHeliteMappingCatalog] = useState({
    products: [],
    categories: [],
    subcategories: [],
  });
  const [heliteMappingCatalogLoading, setHeliteMappingCatalogLoading] = useState(false);
  const [heliteMappingRooms, setHeliteMappingRooms] = useState([]);
  const [heliteMappingRoomsLoading, setHeliteMappingRoomsLoading] = useState(false);
  const [heliteMappingLoading, setHeliteMappingLoading] = useState(false);
  const [heliteMappingSaving, setHeliteMappingSaving] = useState(false);
  const [heliteMappingDirty, setHeliteMappingDirty] = useState(false);
  const [draggedMappingTargetKey, setDraggedMappingTargetKey] = useState("");
  const [selectedMappingTargetKey, setSelectedMappingTargetKey] = useState("");
  const [heliteMappingDialog, setHeliteMappingDialog] = useState({
    open: false,
    target: null,
    structure: null,
    debit: null,
    roomIds: [],
    timeSlotNames: [],
  });

  const [smtpData, setSmtpData] = useState({
    indirizzoEmail: "",
    userLogin: "",
    passwordLogin: "",
    serverSmtp: "",
    portaSmtp: "",
    hasSmtpPassword: false,
  });

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const translateTimeSlotLabel = useCallback((label) => {
    const normalized = String(label || "").trim().toUpperCase();

    if (normalized === "BREAKFAST") return t("Breakfast");
    if (normalized === "LUNCH") return t("Lunch");
    if (normalized === "DINNER") return t("Dinner");

    return label || "";
  }, [t]);

  const normalizeTimeSlotLabel = (value) => {
    const raw = String(value || "").trim();
    const upper = raw.toUpperCase();

    if (upper === "BREAKFAST" || upper === "COLAZIONE" || upper === "MENGJES" || upper === "MËNGJES") {
      return "BREAKFAST";
    }

    if (upper === "LUNCH" || upper === "PRANZO" || upper === "DREKE" || upper === "DREKË") {
      return "LUNCH";
    }

    if (upper === "DINNER" || upper === "CENA" || upper === "DARKE" || upper === "DARKË") {
      return "DINNER";
    }

    return raw;
  };

  const fetchSettings = useCallback(
    async (silent = false) => {
      if (!token || !tenant) {
        setLoadError(
          t("systemSettings.errors.authMissing", {
            defaultValue: "Sessione non valida. Effettua nuovamente il login.",
          })
        );
        setIsLoading(false);
        return;
      }

      try {
        if (!silent) setIsLoading(true);

        const settingsPromise = getTenantSettings(token, tenant);
        const printersPromise = silent ? Promise.resolve(null) : getConfiguredPrinters(token, tenant);
        const [settings, printers] = await Promise.all([settingsPromise, printersPromise]);

        setShowOrderImages(settings?.showProductImages !== false);
        setShowFixedMenuNameOnKitchenPrint(settings?.showFixedMenuNameOnKitchenPrint === true);
        setOrderPrintFontSize(normalizeOrderPrintFontSize(settings?.orderPrintFontSize));
        setDefaultTableMapView(BACKEND_TABLE_VIEW_TO_UI[settings?.defaultTableView] || "standard");
        setDraftPrinterTargets(Array.isArray(settings?.draftPrinterTargets) ? settings.draftPrinterTargets : []);
        setHeliteBuonoPrinterTargets(Array.isArray(settings?.heliteBuonoPrinterTargets) ? settings.heliteBuonoPrinterTargets : []);
        setVoucherPrinterTargets(Array.isArray(settings?.voucherPrinterTargets) ? settings.voucherPrinterTargets : []);
        setInvoiceCourtesyPrinterTargets(Array.isArray(settings?.invoiceCourtesyPrinterTargets) ? settings.invoiceCourtesyPrinterTargets : []);
        setEventConfirmedColor(settings?.eventConfirmedColor || "#2E7D32");
        setEventInProgressColor(settings?.eventInProgressColor || "#F59E0B");
        setEventCompletedColor(settings?.eventCompletedColor || "#16A34A");
        setQuoteExpiredColor(settings?.quoteExpiredColor || "#DC2626");
        setDefaultVatRate(String(settings?.defaultVatRate ?? 10));
        setTenantLogo({
          base64: settings?.logoBase64 || settings?.tenantLogoBase64 || settings?.logo || "",
          fileName: settings?.logoFileName || settings?.tenantLogoFileName || "",
        });
        setTenantLogoFile(null);
        if (Array.isArray(printers)) {
          setConfiguredPrinters(
          Array.isArray(printers)
            ? printers.map((p) => (typeof p === "string" ? p : p?.name)).filter(Boolean)
            : []
          );
        }

        const mappedTimeSlots = Array.isArray(settings?.timeSlots)
          ? settings.timeSlots
            .map((slot) => ({
              id: slot?.id || crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
              label: slot?.name || "",
              start: slot?.start || "08:00",
              end: slot?.end || "12:00",
            }))
            .filter((slot) => slot.label || slot.start || slot.end)
          : [];

        setTimeSlots(
          mappedTimeSlots.length
            ? mappedTimeSlots
            : [
              { id: crypto?.randomUUID?.() || `${Date.now()}-1`, label: "BREAKFAST", start: "08:00", end: "12:00" },
              { id: crypto?.randomUUID?.() || `${Date.now()}-2`, label: "LUNCH", start: "12:00", end: "15:00" },
              { id: crypto?.randomUUID?.() || `${Date.now()}-3`, label: "DINNER", start: "19:00", end: "23:00" },
            ]
        );

        setBillingData({
          ownerName: settings?.ownerName || "",
          ownerSurname: settings?.ownerSurname || "",
          ownerTaxCode: settings?.taxCode || "",
          ownerVat: settings?.vatNumber || "",
          ownerAddress: settings?.address || "",
          ownerPostalCode: settings?.cap || "",
          ownerCity: settings?.city || "",
          ownerProvince: settings?.province || "",
          ownerEmail: settings?.email || "",
          ownerPhone: settings?.phone || "",
        });

        setEasyInvoiceLogin({
          user: settings?.username || "",
          client_id: settings?.client_id || "",
          secret: settings?.secret || "",
        })

        setHeliteData(mapHeliteSettingsToUi(settings));
        setHeliteSettingsSaved(hasSavedHeliteSettings(settings));

        setSmtpData({
          indirizzoEmail: settings?.indirizzoEmail || "",
          userLogin: settings?.userLogin || "",
          passwordLogin: "",
          serverSmtp: settings?.serverSmtp || "",
          portaSmtp: settings?.portaSmtp ? String(settings.portaSmtp) : "",
          hasSmtpPassword: Boolean(settings?.hasSmtpPassword),
        });

        setLoadError("");
      } catch (err) {
        const message = italianErrorMessage(err, t("systemSettings.errors.fetch", {
          defaultValue: "Errore nel recupero delle impostazioni.",
        }));
        setLoadError(message);
        if (!silent) showSnackbarRef.current(message, "error");
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [token, tenant, t]
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const normalizeHeliteMappingRule = useCallback((mapping) => ({
    dbId: mapping.id,
    scopeType: mapping.targetType || mapping.scopeType,
    scopeId: mapping.targetId ?? mapping.scopeId ?? null,
    scopeLabel: mapping.scopeLabel || "",
    structCode: mapping.structCode,
    structName: mapping.structName || mapping.structCode,
    dep: mapping.dep,
    depName: mapping.depName || mapping.dep,
    service: mapping.service,
    serviceName: mapping.serviceName || mapping.service,
    roomIds: normalizeIdArray(mapping.roomIds),
    timeSlotNames: normalizeNameArray(mapping.timeSlotNames),
    vatPercent: normalizeVatPercent(mapping.vatPercent),
    vatCode: mapping.vatCode || null,
    vatDescription: mapping.vatDescription || null,
    updatedAt: mapping.updatedAt,
  }), []);

  const loadHeliteServiceMappings = useCallback(async () => {
    if (!token || !tenant) return;

    setHeliteMappingLoading(true);
    try {
      const mappings = await getHeliteServiceMappings(token, tenant);
      const rules = (Array.isArray(mappings) ? mappings : []).map(normalizeHeliteMappingRule);
      setHeliteMappingRules(rules);
      setSavedHeliteMappingRules(rules);
      setHeliteMappingDirty(false);
    } catch (err) {
      setHeliteMappingRules([]);
      setSavedHeliteMappingRules([]);
      setHeliteMappingDirty(false);
      showSnackbarRef.current(
        err.message || t("systemSettings.pms.mapping.loadError", { defaultValue: "Errore nel caricamento mapping HElite." }),
        "error"
      );
    } finally {
      setHeliteMappingLoading(false);
    }
  }, [normalizeHeliteMappingRule, token, tenant, t]);

  const loadHeliteMappingRooms = useCallback(async () => {
    if (!token || !tenant) return;

    setHeliteMappingRoomsLoading(true);
    try {
      const rooms = await getAllStanze(token, tenant, { noCache: true });
      setHeliteMappingRooms(Array.isArray(rooms) ? rooms : []);
    } catch (err) {
      setHeliteMappingRooms([]);
      showSnackbarRef.current(
        err.message || t("systemSettings.pms.mapping.roomsLoadError", { defaultValue: "Errore nel caricamento sale." }),
        "error"
      );
    } finally {
      setHeliteMappingRoomsLoading(false);
    }
  }, [token, tenant, t]);

  const loadHeliteMappingCatalog = useCallback(async () => {
    if (!token || !tenant) return;

    setHeliteMappingCatalogLoading(true);
    try {
      const [products, categories, subcategories] = await Promise.all([
        getAllProdotti(token, tenant).catch(() => []),
        getAllCategorie(token, tenant).catch(() => []),
        getAllSottocategorie(token, tenant).catch(() => []),
      ]);

      setHeliteMappingCatalog({
        products: Array.isArray(products) ? products : [],
        categories: Array.isArray(categories) ? categories : [],
        subcategories: Array.isArray(subcategories) ? subcategories : [],
      });
    } finally {
      setHeliteMappingCatalogLoading(false);
    }
  }, [token, tenant]);

  const loadHeliteChargeCodes = useCallback(async ({ silent = false } = {}) => {
    if (!token || !tenant) return;

    setHeliteChargeCodesLoading(true);
    try {
      if (!heliteSettingsSaved) {
        throw new Error(t("systemSettings.pms.configurationRequired", {
          defaultValue: "Configurazione HElite non salvata: premi Modifica, inserisci le credenziali corrette e salva prima di ricaricare il catalogo.",
        }));
      }

      const response = await getHeliteChargeCodes(token, tenant);
      const structures = Array.isArray(response?.structures) ? response.structures : [];
      setHeliteChargeCodes(structures);

      if (structures.length === 1) {
        setHeliteData((prev) => ({
          ...prev,
          heliteStructCode: prev.heliteStructCode || structures[0].code || "",
        }));
      }

      if (!silent) {
        showSnackbarRef.current(
          t("systemSettings.pms.codesLoaded", {
            defaultValue: "Codici HElite caricati.",
          }),
          "success"
        );
      }
    } catch (err) {
      setHeliteChargeCodes([]);
      if (!silent) {
        showSnackbarRef.current(
          italianErrorMessage(err, t("systemSettings.pms.codesLoadError", {
            defaultValue: "Errore durante il caricamento dei codici HElite.",
          })),
          "error"
        );
      }
    } finally {
      setHeliteChargeCodesLoading(false);
    }
  }, [heliteSettingsSaved, token, tenant, t]);

  useEffect(() => {
    if (activeSection === "pms") {
      loadHeliteMappingCatalog();
      loadHeliteMappingRooms();
      loadHeliteServiceMappings();
      loadHeliteChargeCodes({ silent: true });
    }
  }, [activeSection, loadHeliteChargeCodes, loadHeliteMappingCatalog, loadHeliteMappingRooms, loadHeliteServiceMappings]);

  useEffect(() => {
    const bc = new BroadcastChannel("ws-events");
    const handler = ({ data }) => {
      if (data?.event === "settings_updated") fetchSettings(true);
    };
    bc.addEventListener("message", handler);
    return () => {
      bc.removeEventListener("message", handler);
      bc.close();
    };
  }, [fetchSettings]);

  const broadcastSettingsUpdated = () => {
    const bc = new BroadcastChannel("ws-events");
    bc.postMessage({ event: "settings_updated" });
    bc.close();
  };

  const persistSettings = async (patch, previousUiState) => {
    if (!token || !tenant) {
      showSnackbar(
        t("systemSettings.errors.authMissing", {
          defaultValue: "Sessione non valida. Effettua nuovamente il login.",
        }),
        "error"
      );
      return false;
    }

    setIsSaving(true);
    setLoadError("");

    try {
      const updated = await updateTenantSettings(patch, token, tenant);

      setShowOrderImages(updated?.showProductImages !== false);
      setShowFixedMenuNameOnKitchenPrint(updated?.showFixedMenuNameOnKitchenPrint === true);
      setOrderPrintFontSize(normalizeOrderPrintFontSize(updated?.orderPrintFontSize));
      setDefaultTableMapView(BACKEND_TABLE_VIEW_TO_UI[updated?.defaultTableView] || "standard");
      setDraftPrinterTargets(Array.isArray(updated?.draftPrinterTargets) ? updated.draftPrinterTargets : []);
      setHeliteBuonoPrinterTargets(Array.isArray(updated?.heliteBuonoPrinterTargets) ? updated.heliteBuonoPrinterTargets : []);
      setVoucherPrinterTargets(Array.isArray(updated?.voucherPrinterTargets) ? updated.voucherPrinterTargets : []);
      setInvoiceCourtesyPrinterTargets(Array.isArray(updated?.invoiceCourtesyPrinterTargets) ? updated.invoiceCourtesyPrinterTargets : []);
      setDefaultVatRate(String(updated?.defaultVatRate ?? 10));

      if (Object.prototype.hasOwnProperty.call(patch, "timeSlots")) {
        setTimeSlots(
          Array.isArray(updated?.timeSlots)
            ? updated.timeSlots.map((slot) => ({
              id: slot?.id || crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
              label: slot?.name || "",
              start: slot?.start || "08:00",
              end: slot?.end || "12:00",
            }))
            : []
        );
      }
      setEventConfirmedColor(updated?.eventConfirmedColor || "#2E7D32");
      setEventInProgressColor(updated?.eventInProgressColor || "#F59E0B");
      setEventCompletedColor(updated?.eventCompletedColor || "#16A34A");
      setQuoteExpiredColor(updated?.quoteExpiredColor || "#DC2626");
      setTenantLogo({
        base64: updated?.logoBase64 || updated?.tenantLogoBase64 || updated?.logo || tenantLogo.base64 || "",
        fileName: updated?.logoFileName || updated?.tenantLogoFileName || tenantLogo.fileName || "",
      });

      if (
        Object.prototype.hasOwnProperty.call(patch, "ownerName") ||
        Object.prototype.hasOwnProperty.call(patch, "ownerSurname") ||
        Object.prototype.hasOwnProperty.call(patch, "taxCode") ||
        Object.prototype.hasOwnProperty.call(patch, "vatNumber") ||
        Object.prototype.hasOwnProperty.call(patch, "address") ||
        Object.prototype.hasOwnProperty.call(patch, "cap") ||
        Object.prototype.hasOwnProperty.call(patch, "city") ||
        Object.prototype.hasOwnProperty.call(patch, "province") ||
        Object.prototype.hasOwnProperty.call(patch, "email") ||
        Object.prototype.hasOwnProperty.call(patch, "phone")
      ) {
        setBillingData({
          ownerName: updated?.ownerName || "",
          ownerSurname: updated?.ownerSurname || "",
          ownerTaxCode: updated?.taxCode || "",
          ownerVat: updated?.vatNumber || "",
          ownerAddress: updated?.address || "",
          ownerPostalCode: updated?.cap || "",
          ownerCity: updated?.city || "",
          ownerProvince: updated?.province || "",
          ownerEmail: updated?.email || "",
          ownerPhone: updated?.phone || "",
        });
      }

      if (
        Object.prototype.hasOwnProperty.call(patch, "indirizzoEmail") ||
        Object.prototype.hasOwnProperty.call(patch, "userLogin") ||
        Object.prototype.hasOwnProperty.call(patch, "passwordLogin") ||
        Object.prototype.hasOwnProperty.call(patch, "serverSmtp") ||
        Object.prototype.hasOwnProperty.call(patch, "portaSmtp")
      ) {
        setSmtpData({
          indirizzoEmail: updated?.indirizzoEmail || "",
          userLogin: updated?.userLogin || "",
          passwordLogin: "",
          serverSmtp: updated?.serverSmtp || "",
          portaSmtp: updated?.portaSmtp ? String(updated.portaSmtp) : "",
          hasSmtpPassword: Boolean(updated?.hasSmtpPassword),
        });
      }

      if (
        Object.prototype.hasOwnProperty.call(patch, "heliteUrl") ||
        Object.prototype.hasOwnProperty.call(patch, "heliteClientId") ||
        Object.prototype.hasOwnProperty.call(patch, "heliteUsername") ||
        Object.prototype.hasOwnProperty.call(patch, "helitePassword") ||
        Object.prototype.hasOwnProperty.call(patch, "heliteHotelCode") ||
        Object.prototype.hasOwnProperty.call(patch, "heliteStructCode") ||
        Object.prototype.hasOwnProperty.call(patch, "heliteDepCode") ||
        Object.prototype.hasOwnProperty.call(patch, "heliteServiceCode")
      ) {
        setHeliteData(mapHeliteSettingsToUi(updated));
        setHeliteSettingsSaved(hasSavedHeliteSettings(updated));
      }

      broadcastSettingsUpdated();
      return true;
    } catch (err) {
      if (previousUiState) {
        if (Object.prototype.hasOwnProperty.call(previousUiState, "showOrderImages")) {
          setShowOrderImages(previousUiState.showOrderImages);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "showFixedMenuNameOnKitchenPrint")) {
          setShowFixedMenuNameOnKitchenPrint(previousUiState.showFixedMenuNameOnKitchenPrint);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "orderPrintFontSize")) {
          setOrderPrintFontSize(previousUiState.orderPrintFontSize);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "defaultTableMapView")) {
          setDefaultTableMapView(previousUiState.defaultTableMapView);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "draftPrinterTargets")) {
          setDraftPrinterTargets(previousUiState.draftPrinterTargets || []);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "heliteBuonoPrinterTargets")) {
          setHeliteBuonoPrinterTargets(previousUiState.heliteBuonoPrinterTargets || []);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "voucherPrinterTargets")) {
          setVoucherPrinterTargets(previousUiState.voucherPrinterTargets || []);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "invoiceCourtesyPrinterTargets")) {
          setInvoiceCourtesyPrinterTargets(previousUiState.invoiceCourtesyPrinterTargets || []);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "eventConfirmedColor")) {
          setEventConfirmedColor(previousUiState.eventConfirmedColor || "#2E7D32");
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "eventInProgressColor")) {
          setEventInProgressColor(previousUiState.eventInProgressColor || "#F59E0B");
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "eventCompletedColor")) {
          setEventCompletedColor(previousUiState.eventCompletedColor || "#16A34A");
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "quoteExpiredColor")) {
          setQuoteExpiredColor(previousUiState.quoteExpiredColor || "#DC2626");
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "defaultVatRate")) {
          setDefaultVatRate(previousUiState.defaultVatRate);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "billingData")) {
          setBillingData(previousUiState.billingData);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "timeSlots")) {
          setTimeSlots(previousUiState.timeSlots || []);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "tenantLogo")) {
          setTenantLogo(previousUiState.tenantLogo || { base64: "", fileName: "" });
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "heliteData")) {
          setHeliteData(previousUiState.heliteData);
        }
        if (Object.prototype.hasOwnProperty.call(previousUiState, "smtpData")) {
          setSmtpData(previousUiState.smtpData);
        }
      }

      const message = italianErrorMessage(err, t("systemSettings.errors.save", {
        defaultValue: "Errore nel salvataggio delle impostazioni.",
      }));
      setLoadError(message);
      showSnackbar(message, "error");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const currentQuickSettingsState = () => ({
    showOrderImages,
    showFixedMenuNameOnKitchenPrint,
    orderPrintFontSize,
    defaultTableMapView,
    draftPrinterTargets,
    heliteBuonoPrinterTargets,
    voucherPrinterTargets,
    invoiceCourtesyPrinterTargets,
    eventConfirmedColor,
    eventInProgressColor,
    eventCompletedColor,
    quoteExpiredColor,
    tenantLogo,
  });

  const handleToggle = (_, checked) => {
    const previousUiState = currentQuickSettingsState();
    setShowOrderImages(checked);
    persistSettings({ showProductImages: checked }, previousUiState);
  };

  const handleKitchenMenuNameToggle = (_, checked) => {
    const previousUiState = currentQuickSettingsState();
    setShowFixedMenuNameOnKitchenPrint(checked);
    persistSettings({ showFixedMenuNameOnKitchenPrint: checked }, previousUiState);
  };

  const handleOrderPrintFontSizeChange = (event) => {
    const value = normalizeOrderPrintFontSize(event.target.value);
    const previousUiState = currentQuickSettingsState();
    setOrderPrintFontSize(value);
    persistSettings({ orderPrintFontSize: value }, previousUiState);
  };

  const handleDefaultTableMapViewChange = (event) => {
    const value = event.target.value;
    const previousUiState = currentQuickSettingsState();
    setDefaultTableMapView(value);
    persistSettings({ defaultTableView: UI_TABLE_VIEW_TO_BACKEND[value] || "STANDARD" }, previousUiState);
  };

  const handleDraftPrinterTargetsChange = (event) => {
    const value = Array.isArray(event.target.value) ? event.target.value : [];
    const previousUiState = currentQuickSettingsState();
    setDraftPrinterTargets(value);
    persistSettings({ draftPrinterTargets: value }, previousUiState);
  };

  const handleHeliteBuonoPrinterTargetsChange = (event) => {
    const value = Array.isArray(event.target.value) ? event.target.value : [];
    const previousUiState = currentQuickSettingsState();
    setHeliteBuonoPrinterTargets(value);
    persistSettings({ heliteBuonoPrinterTargets: value }, previousUiState);
  };

  const handleVoucherPrinterTargetsChange = (event) => {
    const value = Array.isArray(event.target.value) ? event.target.value : [];
    const previousUiState = currentQuickSettingsState();
    setVoucherPrinterTargets(value);
    persistSettings({ voucherPrinterTargets: value }, previousUiState);
  };

  const handleInvoiceCourtesyPrinterTargetsChange = (event) => {
    const value = Array.isArray(event.target.value) ? event.target.value : [];
    const previousUiState = currentQuickSettingsState();
    setInvoiceCourtesyPrinterTargets(value);
    persistSettings({ invoiceCourtesyPrinterTargets: value }, previousUiState);
  };

  const handleSaveDefaultVatRate = () => {
    const parsed = Number(String(defaultVatRate).replace(",", "."));
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      showSnackbar(t("systemSettings.defaultVatRateInvalid"), "warning");
      return;
    }

    const previousVatRate = defaultVatRate;
    setDefaultVatRate(String(parsed));
    showSnackbar(t("systemSettings.defaultVatRateSaved"), "success");

    updateTenantSettings(
      { defaultVatRate: parsed, showProductImages: showOrderImages },
      token,
      tenant
    )
      .then(() => {
        broadcastSettingsUpdated();
      })
      .catch((err) => {
        setDefaultVatRate(previousVatRate);
        showSnackbar(
          italianErrorMessage(err, t("systemSettings.errors.save", {
            defaultValue: "Errore nel salvataggio delle impostazioni.",
          })),
          "error"
        );
      });
  };

  const handleEventColorChange = (field, value) => {
    const fallbackByField = {
      eventConfirmedColor: "#2E7D32",
      eventInProgressColor: "#F59E0B",
      eventCompletedColor: "#16A34A",
      quoteExpiredColor: "#DC2626",
    };
    const normalized = value || fallbackByField[field] || "#F59E0B";
    const previousUiState = currentQuickSettingsState();
    if (field === "eventConfirmedColor") {
      setEventConfirmedColor(normalized);
    } else if (field === "eventInProgressColor") {
      setEventInProgressColor(normalized);
    } else if (field === "eventCompletedColor") {
      setEventCompletedColor(normalized);
    } else {
      setQuoteExpiredColor(normalized);
    }
    persistSettings({ [field]: normalized }, previousUiState);
  };

  const handleLogoFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      showSnackbar(
        t("systemSettings.logo.invalidType", {
          defaultValue: "Carica un logo PNG, JPG, WEBP o SVG.",
        }),
        "warning"
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar(
        t("systemSettings.logo.tooLarge", {
          defaultValue: "Il logo deve essere inferiore a 5 MB.",
        }),
        "warning"
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = typeof reader.result === "string" ? reader.result : "";
      if (!base64) return;
      setTenantLogo({ base64, fileName: file.name });
      setTenantLogoFile(file);
    };
    reader.onerror = () => {
      showSnackbar(
        t("systemSettings.logo.readError", {
          defaultValue: "Impossibile leggere il file selezionato.",
        }),
        "error"
      );
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async () => {
    if (!tenantLogoFile) {
      showSnackbar(
        t("systemSettings.logo.selectFirst", {
          defaultValue: "Seleziona un nuovo logo prima di salvare.",
        }),
        "warning"
      );
      return;
    }

    const token = localStorage.getItem("accessToken");
    const tenant = localStorage.getItem("tenantId");

    if (!token || !tenant) {
      showSnackbar(
        t("systemSettings.errors.authMissing", {
          defaultValue: "Sessione non valida. Effettua nuovamente il login.",
        }),
        "error"
      );
      return;
    }

    setIsSaving(true);
    setLoadError("");
    try {
      const updated = await uploadTenantLogo(tenantLogoFile, token, tenant);
      setTenantLogo({
        base64: updated?.logoBase64 || updated?.tenantLogoBase64 || updated?.logo || "",
        fileName: updated?.logoFileName || updated?.tenantLogoFileName || "",
      });
      setTenantLogoFile(null);
      broadcastSettingsUpdated();
      showSnackbar(
        t("systemSettings.logo.saveSuccess", {
          defaultValue: "Logo salvato con successo.",
        }),
        "success"
      );
    } catch (err) {
      const message = italianErrorMessage(err, t("systemSettings.logo.saveError", {
        defaultValue: "Errore durante il salvataggio del logo.",
      }));
      setLoadError(message);
      showSnackbar(
        message,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    const previousLogo = { ...tenantLogo };
    const previousFile = tenantLogoFile;
    const token = localStorage.getItem("accessToken");
    const tenant = localStorage.getItem("tenantId");

    if (!token || !tenant) {
      showSnackbar(
        t("systemSettings.errors.authMissing", {
          defaultValue: "Sessione non valida. Effettua nuovamente il login.",
        }),
        "error"
      );
      return;
    }

    setTenantLogo({ base64: "", fileName: "" });
    setTenantLogoFile(null);
    setIsSaving(true);
    setLoadError("");
    try {
      const updated = await deleteTenantLogo(token, tenant);
      setTenantLogo({
        base64: updated?.logoBase64 || updated?.tenantLogoBase64 || updated?.logo || "",
        fileName: updated?.logoFileName || updated?.tenantLogoFileName || "",
      });
      broadcastSettingsUpdated();
      showSnackbar(
        t("systemSettings.logo.removeSuccess", {
          defaultValue: "Logo rimosso con successo.",
        }),
        "success"
      );
    } catch (err) {
      setTenantLogo(previousLogo);
      setTenantLogoFile(previousFile);
      const message = italianErrorMessage(err, t("systemSettings.logo.removeError", {
        defaultValue: "Errore durante la rimozione del logo.",
      }));
      setLoadError(message);
      showSnackbar(
        message,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBillingChange = (field, value) => {
    setBillingData((prev) => ({ ...prev, [field]: value }));
  };
  const handleEasyInvoiceChange = (field, value) => {
    setEasyInvoiceLogin((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeliteChange = (field, value) => {
    setHeliteData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "heliteStructCode" ? { heliteDepCode: "", heliteServiceCode: "" } : {}),
      ...(field === "heliteDepCode" ? { heliteServiceCode: "" } : {}),
    }));
  };

  const heliteMappingTargets = useMemo(() => {
    const categoryById = new Map((heliteMappingCatalog.categories || []).map((item) => [Number(item.id), item]));
    const defaultVat = normalizeVatPercent(defaultVatRate) ?? 10;
    const categoryVatRate = (category) => normalizeVatPercent(category?.vatRate ?? defaultVat) ?? defaultVat;
    const productVatRate = (product) => {
      const rates = (Array.isArray(product?.categories) ? product.categories : [])
        .map((entry) => normalizeVatPercent(entry?.category?.vatRate ?? defaultVat))
        .filter((value) => value !== null);
      if (rates.length) return Math.max(...rates);
      const category = categoryById.get(Number(product?.categoryId));
      return categoryVatRate(category);
    };

    const categoryTargets = (heliteMappingCatalog.categories || []).map((category) => ({
      scopeType: "CATEGORY",
      scopeId: category.id,
      label: category.name,
      helper: t("systemSettings.pms.mapping.category", { defaultValue: "Categoria" }),
      vatRate: categoryVatRate(category),
    }));

    const subcategoryTargets = (heliteMappingCatalog.subcategories || []).map((subcategory) => {
      const parent = categoryById.get(Number(subcategory.categoryId));
      return {
        scopeType: "SUBCATEGORY",
        scopeId: subcategory.id,
        label: subcategory.name,
        helper: parent?.name
          ? `${t("systemSettings.pms.mapping.subcategory", { defaultValue: "Sottocategoria" })} - ${parent.name}`
          : t("systemSettings.pms.mapping.subcategory", { defaultValue: "Sottocategoria" }),
        vatRate: categoryVatRate(parent),
      };
    });

    const productTargets = (heliteMappingCatalog.products || []).map((product) => ({
      scopeType: "PRODUCT",
      scopeId: product.id,
      label: product.name,
      helper: [product.categoryName, product.subcategoryName].filter(Boolean).join(" / ")
        || t("systemSettings.pms.mapping.product", { defaultValue: "Prodotto" }),
      vatRate: productVatRate(product),
    }));

    const localVatRates = [...new Set([
      defaultVat,
      ...categoryTargets.map((target) => target.vatRate),
      ...subcategoryTargets.map((target) => target.vatRate),
      ...productTargets.map((target) => target.vatRate),
    ].filter((value) => value !== null).map((value) => String(value)))].map(Number).sort((a, b) => a - b);

    const defaultTargets = localVatRates.map((vatRate) => ({
        scopeType: "DEFAULT",
        scopeId: null,
        label: t("systemSettings.pms.mapping.defaultSale", { defaultValue: "Vendita default" }),
        helper: t("systemSettings.pms.mapping.defaultSaleHelp", { defaultValue: "Fallback quando non esiste una regola piu specifica." }),
        vatRate,
    }));

    return [
      ...defaultTargets,
      ...categoryTargets,
      ...subcategoryTargets,
      ...productTargets,
    ];
  }, [defaultVatRate, heliteMappingCatalog, t]);

  const heliteMappingTargetByKey = useMemo(
    () => new Map(heliteMappingTargets.map((target) => [mappingTargetKey(target), target])),
    [heliteMappingTargets]
  );

  const selectedMappingTarget = useMemo(
    () => heliteMappingTargetByKey.get(selectedMappingTargetKey) || null,
    [heliteMappingTargetByKey, selectedMappingTargetKey]
  );

  const heliteMappingRoomById = useMemo(
    () => new Map((heliteMappingRooms || []).map((room) => [Number(room.id), room])),
    [heliteMappingRooms]
  );

  const configuredTimeSlotNames = useMemo(
    () => normalizeNameArray((timeSlots || []).map((slot) => slot.label)),
    [timeSlots]
  );

  const getHeliteRuleLabel = useCallback((rule) => (
    rule.scopeLabel || heliteMappingTargetByKey.get(mappingTargetKey(rule))?.label || `${rule.scopeType} ${rule.scopeId || ""}`.trim()
  ), [heliteMappingTargetByKey]);

  const getHeliteRuleContextLabel = useCallback((rule) => {
    const roomIds = normalizeIdArray(rule?.roomIds);
    const timeSlotNames = normalizeNameArray(rule?.timeSlotNames);
    const roomLabel = roomIds.length
      ? roomIds.map((roomId) => heliteMappingRoomById.get(roomId)?.name || `Sala ${roomId}`).join(", ")
      : t("systemSettings.pms.mapping.allRooms", { defaultValue: "Tutte le sale" });
    const timeSlotLabel = timeSlotNames.length
      ? timeSlotNames.map(translateTimeSlotLabel).join(", ")
      : t("systemSettings.pms.mapping.allTimeSlots", { defaultValue: "Tutte le fasce" });

    return `${roomLabel} | ${timeSlotLabel}`;
  }, [heliteMappingRoomById, t, translateTimeSlotLabel]);

  const activeHeliteServiceKeys = useMemo(() => {
    const keys = new Set();
    (heliteChargeCodes || []).forEach((structure) => {
      (structure?.debits || []).forEach((debit) => {
        keys.add(debitKey(structure.code, debit));
      });
    });
    return keys;
  }, [heliteChargeCodes]);

  const hasLoadedHeliteServiceCatalog = heliteChargeCodes.length > 0;

  const isHeliteMappingInactive = useCallback((rule) => (
    hasLoadedHeliteServiceCatalog && !activeHeliteServiceKeys.has(ruleDebitKey(rule))
  ), [activeHeliteServiceKeys, hasLoadedHeliteServiceCatalog]);

  const inactiveHeliteMappingRules = useMemo(
    () => (heliteMappingRules || []).filter(isHeliteMappingInactive),
    [heliteMappingRules, isHeliteMappingInactive]
  );

  const heliteMappingRulesByTarget = useMemo(
    () => {
      const byTarget = new Map();
      (heliteMappingRules || []).forEach((rule) => {
        const key = mappingTargetKey(rule);
        byTarget.set(key, [...(byTarget.get(key) || []), rule]);
      });
      return byTarget;
    },
    [heliteMappingRules]
  );

  const heliteMappingsByDebit = useMemo(() => {
    const byDebit = new Map();
    (heliteMappingRules || []).forEach((rule) => {
      const key = debitKey(rule.structCode, { dep: rule.dep, service: rule.service });
      byDebit.set(key, [...(byDebit.get(key) || []), rule]);
    });
    return byDebit;
  }, [heliteMappingRules]);

  const updateHeliteMappingDraft = useCallback((nextRules) => {
    setHeliteMappingRules(nextRules);
    setHeliteMappingDirty(true);
  }, []);

  const buildHeliteMappingPayload = (rule) => ({
    targetType: rule.scopeType,
    targetId: rule.scopeType === "DEFAULT" ? null : Number(rule.scopeId),
    structCode: rule.structCode,
    dep: rule.dep,
    service: rule.service,
    roomIds: normalizeIdArray(rule.roomIds),
    timeSlotNames: normalizeNameArray(rule.timeSlotNames),
    vatPercent: normalizeVatPercent(rule.vatPercent),
    vatCode: rule.vatCode || null,
    vatDescription: rule.vatDescription || null,
  });

  const mappingChanged = (current, saved) => {
    if (!saved) return true;
    return current.structCode !== saved.structCode
      || current.dep !== saved.dep
      || current.service !== saved.service
      || !arraysEqual(normalizeIdArray(current.roomIds), normalizeIdArray(saved.roomIds))
      || !arraysEqual(normalizeNameArray(current.timeSlotNames), normalizeNameArray(saved.timeSlotNames))
      || !ratesMatch(current.vatPercent, saved.vatPercent)
      || (current.vatCode || "") !== (saved.vatCode || "")
      || (current.vatDescription || "") !== (saved.vatDescription || "");
  };

  const saveHeliteMappingRules = async () => {
    if (!token || !tenant) return;

    setHeliteMappingSaving(true);
    try {
      const draftRules = heliteMappingRules || [];
      const savedByKey = new Map((savedHeliteMappingRules || []).map((rule) => [mappingRuleKey(rule), rule]));
      const draftByKey = new Map(draftRules.map((rule) => [mappingRuleKey(rule), rule]));

      const mappingsToDelete = (savedHeliteMappingRules || []).filter((rule) => !draftByKey.has(mappingRuleKey(rule)));
      const mappingsToSave = draftRules.filter((rule) => mappingChanged(rule, savedByKey.get(mappingRuleKey(rule))));

      await Promise.all([
        ...mappingsToDelete
          .filter((rule) => rule.dbId)
          .map((rule) => deleteHeliteServiceMapping(token, tenant, rule.dbId)),
        ...mappingsToSave.map((rule) => saveHeliteServiceMapping(token, tenant, buildHeliteMappingPayload(rule))),
      ]);

      await loadHeliteServiceMappings();
      showSnackbar(
        t("systemSettings.pms.mapping.saved", { defaultValue: "Mapping HElite salvato." }),
        "success"
      );
    } catch (err) {
      showSnackbar(
        err.message || t("systemSettings.pms.mapping.saveError", { defaultValue: "Errore nel salvataggio mapping HElite." }),
        "error"
      );
    } finally {
      setHeliteMappingSaving(false);
    }
  };

  const resetHeliteMappingDraft = () => {
    setHeliteMappingRules(savedHeliteMappingRules);
    setHeliteMappingDirty(false);
    setSelectedMappingTargetKey("");
  };

  const closeHeliteMappingDialog = () => {
    setHeliteMappingDialog({
      open: false,
      target: null,
      structure: null,
      debit: null,
      roomIds: [],
      timeSlotNames: [],
    });
  };

  const assignHeliteMapping = useCallback((target, structure, debit) => {
    if (!target || !structure || !debit?.dep || !debit?.service) return;
    const targetVatRate = normalizeVatPercent(target.vatRate);
    const debitVatPercent = normalizeVatPercent(debit.vatPercent);

    if (targetVatRate === null || debitVatPercent === null) {
      showSnackbar(
        t("systemSettings.pms.mapping.vatMissing", { defaultValue: "IVA target o servizio HElite mancante." }),
        "warning"
      );
      return;
    }

    if (!ratesMatch(targetVatRate, debitVatPercent)) {
      showSnackbar(
        t("systemSettings.pms.mapping.vatMismatch", {
          defaultValue: `IVA non compatibile: target ${formatVatPercent(targetVatRate)}%, HElite ${formatVatPercent(debitVatPercent)}%.`,
        }),
        "warning"
      );
      return;
    }

    const candidateRule = {
      scopeType: target.scopeType,
      scopeId: target.scopeId ?? null,
      vatPercent: debitVatPercent,
      structCode: structure.code,
      dep: debit.dep,
      service: debit.service,
    };
    const existingRule = (heliteMappingRules || []).find((rule) => mappingRuleKey(rule) === mappingRuleKey(candidateRule));

    setHeliteMappingDialog({
      open: true,
      target,
      structure,
      debit,
      roomIds: normalizeIdArray(existingRule?.roomIds),
      timeSlotNames: normalizeNameArray(existingRule?.timeSlotNames),
    });
  }, [heliteMappingRules, showSnackbar, t]);

  const confirmHeliteMappingDialog = () => {
    const { target, structure, debit } = heliteMappingDialog;
    if (!target || !structure || !debit?.dep || !debit?.service) return;

    const roomIds = normalizeIdArray(heliteMappingDialog.roomIds);
    const timeSlotNames = normalizeNameArray(heliteMappingDialog.timeSlotNames);

    const debitVatPercent = normalizeVatPercent(debit.vatPercent);
    const candidateRule = {
      scopeType: target.scopeType,
      scopeId: target.scopeId ?? null,
      vatPercent: debitVatPercent,
      structCode: structure.code,
      dep: debit.dep,
      service: debit.service,
    };
    const existingRule = (heliteMappingRules || []).find((rule) => mappingRuleKey(rule) === mappingRuleKey(candidateRule));
    const nextRule = {
      id: [mappingTargetKey(target), debitKey(structure.code, debit)].join("::"),
      dbId: existingRule?.dbId,
      scopeType: target.scopeType,
      scopeId: target.scopeId ?? null,
      scopeLabel: target.label,
      vatPercent: debitVatPercent,
      vatCode: debit.vatCode || null,
      vatDescription: debit.vatDescription || null,
      roomIds,
      timeSlotNames,
      structCode: structure.code,
      structName: structure.name || structure.code,
      dep: debit.dep,
      depName: debit.depName || debit.dep,
      service: debit.service,
      serviceName: debit.serviceName || debit.service,
      updatedAt: new Date().toISOString(),
    };

    const nextRules = [
      ...(heliteMappingRules || []).filter((rule) => mappingRuleKey(rule) !== mappingRuleKey(nextRule)),
      nextRule,
    ].sort((a, b) => {
      const order = { DEFAULT: 0, CATEGORY: 1, SUBCATEGORY: 2, PRODUCT: 3 };
      return (order[a.scopeType] || 0) - (order[b.scopeType] || 0)
      || String(a.scopeLabel || "").localeCompare(String(b.scopeLabel || ""))
      || ruleDebitKey(a).localeCompare(ruleDebitKey(b));
    });

    updateHeliteMappingDraft(nextRules);
    setSelectedMappingTargetKey("");
    closeHeliteMappingDialog();
    showSnackbar(
      t("systemSettings.pms.mapping.assigned", { defaultValue: "Mapping HElite aggiornato nel draft." }),
      "success"
    );
  };

  const openHeliteMappingRuleEditor = (rule) => {
    if (!rule) return;
    const target = heliteMappingTargetByKey.get(mappingTargetKey(rule)) || {
      scopeType: rule.scopeType,
      scopeId: rule.scopeId ?? null,
      label: getHeliteRuleLabel(rule),
      vatRate: rule.vatPercent,
    };
    const structure = (heliteChargeCodes || []).find((item) => item.code === rule.structCode) || {
      code: rule.structCode,
      name: rule.structName || rule.structCode,
      debits: [],
    };
    const debit = (structure.debits || []).find((item) => item.dep === rule.dep && item.service === rule.service) || {
      dep: rule.dep,
      depName: rule.depName || rule.dep,
      service: rule.service,
      serviceName: rule.serviceName || rule.service,
      vatPercent: rule.vatPercent,
      vatCode: rule.vatCode || null,
      vatDescription: rule.vatDescription || null,
    };

    setHeliteMappingDialog({
      open: true,
      target,
      structure,
      debit,
      roomIds: normalizeIdArray(rule.roomIds),
      timeSlotNames: normalizeNameArray(rule.timeSlotNames),
    });
  };

  const removeHeliteMapping = (rule) => {
    const nextRules = (heliteMappingRules || []).filter((item) => mappingRuleKey(item) !== mappingRuleKey(rule));
    updateHeliteMappingDraft(nextRules);
  };

  const clearHeliteMappings = () => {
    updateHeliteMappingDraft([]);
    setSelectedMappingTargetKey("");
    showSnackbar(
      t("systemSettings.pms.mapping.cleared", { defaultValue: "Mapping HElite svuotato nel draft." }),
      "success"
    );
  };

  const handleHeliteMappingDragStart = (event, target) => {
    const payload = JSON.stringify(target);
    event.dataTransfer.setData("application/json", payload);
    event.dataTransfer.setData("text/plain", payload);
    event.dataTransfer.effectAllowed = "copy";
    setDraggedMappingTargetKey(mappingTargetKey(target));
  };

  const handleHeliteMappingDrop = (event, structure, debit) => {
    event.preventDefault();
    setDraggedMappingTargetKey("");

    try {
      const payload = event.dataTransfer.getData("application/json") || event.dataTransfer.getData("text/plain");
      const target = JSON.parse(payload);
      assignHeliteMapping(target, structure, debit);
    } catch (_) {
      showSnackbar(
        t("systemSettings.pms.mapping.dropError", { defaultValue: "Target mapping non valido." }),
        "warning"
      );
    }
  };

  const handleHeliteMappingTargetSelect = (target) => {
    const key = mappingTargetKey(target);
    setSelectedMappingTargetKey((prev) => (prev === key ? "" : key));
  };

  const handleHeliteMappingServiceClick = (structure, debit) => {
    if (!selectedMappingTarget) return;
    assignHeliteMapping(selectedMappingTarget, structure, debit);
  };

  const handleSmtpChange = (field, value) => {
    setSmtpData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveBillingData = async () => {
    const ownerName = (billingData.ownerName || "").trim();
    const ownerSurname = (billingData.ownerSurname || "").trim();
    const ownerTaxCode = (billingData.ownerTaxCode || "").trim();
    const ownerVat = (billingData.ownerVat || "").trim();
    const ownerAddress = (billingData.ownerAddress || "").trim();
    const ownerPostalCode = (billingData.ownerPostalCode || "").trim();
    const ownerCity = (billingData.ownerCity || "").trim();
    const ownerProvince = (billingData.ownerProvince || "").trim().toUpperCase();
    const ownerEmail = (billingData.ownerEmail || "").trim();
    const ownerPhone = (billingData.ownerPhone || "").trim();

    if (!ownerName) {
      showSnackbar(
        t("systemSettings.validation.ownerNameRequired", {
          defaultValue: "Inserisci almeno il nome o la ragione sociale.",
        }),
        "warning"
      );
      return;
    }

    if (ownerProvince && ownerProvince.length > 2) {
      showSnackbar(
        t("systemSettings.validation.provinceTooLong", {
          defaultValue: "La provincia deve avere massimo 2 caratteri.",
        }),
        "warning"
      );
      return;
    }

    if (ownerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail)) {
      showSnackbar(
        t("systemSettings.validation.emailInvalid", {
          defaultValue: "Email non valida.",
        }),
        "warning"
      );
      return;
    }

    const patch = {
      ownerName: ownerName || null,
      ownerSurname: ownerSurname || null,
      taxCode: ownerTaxCode || null,
      vatNumber: ownerVat || null,
      address: ownerAddress || null,
      cap: ownerPostalCode || null,
      city: ownerCity || null,
      province: ownerProvince || null,
      email: ownerEmail || null,
      phone: ownerPhone || null,
    };

    const previousUiState = { billingData: { ...billingData } };
    const ok = await persistSettings(patch, previousUiState);

    if (ok) {
      setIsEdit(false);
      showSnackbar(
        t("systemSettings.saveBillingSuccess", {
          defaultValue: "Dati di fatturazione salvati con successo.",
        }),
        "success"
      );
    }
  };

  const handleSaveEasyInvoiceLogin = async () => { //da testare post migration
    const user = (easyInvoiceLogin.user || "").trim();
    const client_id = (easyInvoiceLogin.client_id || "").trim();
    const secret = (easyInvoiceLogin.secret || "").trim();

    const patch = {
      username: user || null,
      client_id: client_id || null,
      secret: secret || null,
    };

    const previousUiState = { easyInvoiceLogin: { ...easyInvoiceLogin } };
    const ok = await persistSettings(patch, previousUiState);

    if (ok) {
      setIsEdit(false);
      showSnackbar(
        t("systemSettings.saveEasyInvoiceSuccess", {
          defaultValue: "Credenziali salvate con successo.",
        }),
        "success"
      );
    }
  }

  const handleSaveHeliteData = async () => {
    const heliteUrl = (heliteData.heliteUrl || "").trim().replace(/\/+$/, "");
    const heliteClientId = (heliteData.heliteClientId || "").trim();
    const heliteUsername = (heliteData.heliteUsername || "").trim();
    const helitePassword = (heliteData.helitePassword || "").trim();
    const heliteHotelCode = (heliteData.heliteHotelCode || "").trim();
    const heliteStructCode = (heliteData.heliteStructCode || "").trim();
    const heliteDepCode = (heliteData.heliteDepCode || "").trim();
    const heliteServiceCode = (heliteData.heliteServiceCode || "").trim();

    if (heliteUrl) {
      try {
        new URL(heliteUrl);
      } catch (_err) {
        showSnackbar(
          t("systemSettings.pms.urlInvalid", {
            defaultValue: "URL HElite non valido.",
          }),
          "warning"
        );
        return;
      }
    }

    const patch = {
      heliteUrl: heliteUrl || null,
      heliteClientId: heliteClientId || null,
      heliteUsername: heliteUsername || null,
      heliteHotelCode: heliteHotelCode || null,
      heliteStructCode: heliteStructCode || null,
      heliteDepCode: heliteDepCode || null,
      heliteServiceCode: heliteServiceCode || null,
    };

    if (helitePassword) {
      patch.helitePassword = helitePassword;
    }

    const previousUiState = { heliteData: { ...heliteData } };
    const ok = await persistSettings(patch, previousUiState);

    if (ok) {
      setIsEdit(false);
      showSnackbar(
        t("systemSettings.pms.saveSuccess", {
          defaultValue: "Configurazione PMS salvata con successo.",
        }),
        "success"
      );
    }
  };

  const handleSaveSmtpData = async () => {
    const indirizzoEmail = (smtpData.indirizzoEmail || "").trim();
    const userLogin = (smtpData.userLogin || "").trim();
    const passwordLogin = (smtpData.passwordLogin || "").trim();
    const serverSmtp = (smtpData.serverSmtp || "").trim();
    const portaSmtp = String(smtpData.portaSmtp || "").trim();

    if (indirizzoEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(indirizzoEmail)) {
      showSnackbar(
        t("systemSettings.validation.emailInvalid", {
          defaultValue: "Email non valida.",
        }),
        "warning"
      );
      return;
    }

    if (portaSmtp) {
      const parsedPort = Number(portaSmtp);
      if (!Number.isInteger(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
        showSnackbar(
          t("systemSettings.smtp.portInvalid", {
            defaultValue: "PORTA_SMTP deve essere un numero tra 1 e 65535.",
          }),
          "warning"
        );
        return;
      }
    }

    const patch = {
      indirizzoEmail: indirizzoEmail || null,
      userLogin: userLogin || null,
      serverSmtp: serverSmtp || null,
      portaSmtp: portaSmtp ? Number(portaSmtp) : null,
    };

    if (passwordLogin) {
      patch.passwordLogin = passwordLogin;
    }

    const previousUiState = { smtpData: { ...smtpData } };
    const ok = await persistSettings(patch, previousUiState);

    if (ok) {
      setIsEdit(false);
      showSnackbar(
        t("systemSettings.smtp.saveSuccess", {
          defaultValue: "Configurazione SMTP salvata con successo.",
        }),
        "success"
      );
    }
  };

  const handleAddTimeSlot = () => {
    setTimeSlots((prev) => [
      ...prev,
      {
        id: crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        label: "",
        start: "08:00",
        end: "12:00",
      },
    ]);
  };

  const handleUpdateTimeSlot = (id, field, value) => {
    setTimeSlots((prev) =>
      prev.map((slot) =>
        slot.id === id
          ? {
            ...slot,
            [field]: field === "label" ? normalizeTimeSlotLabel(value) : value,
          }
          : slot
      )
    );
  };

  const handleRemoveTimeSlot = (id) => {
    setTimeSlots((prev) => prev.filter((slot) => slot.id !== id));
  };

  const handleSaveTimeSlots = async () => {
    const cleaned = timeSlots
      .map((slot) => ({
        id: slot.id,
        name: (slot.label || "").trim(),
        start: slot.start || "",
        end: slot.end || "",
      }))
      .filter((slot) => slot.name && slot.start && slot.end);

    if (!cleaned.length) {
      showSnackbar(t("TimeSlotsRequired"), "warning");
      return;
    }

    for (const slot of cleaned) {
      if (timeToMinutes(slot.start) === null || timeToMinutes(slot.end) === null) {
        showSnackbar(t("TimeSlotInvalid"), "warning");
        return;
      }
    }

    const previousUiState = { timeSlots: [...timeSlots] };
    const ok = await persistSettings({ timeSlots: cleaned }, previousUiState);

    if (ok) {
      showSnackbar(t("TimeSlotsSaved"), "success");
    }
  };

  const sectionItems = [
    {
      key: "general",
      label: t("systemSettings.sectionGeneral", { defaultValue: "Generale" }),
      icon: <SettingsSuggestOutlinedIcon fontSize="small" />,
    },
    {
      key: "billing",
      label: t("systemSettings.sectionBilling", { defaultValue: "Fatturazione" }),
      icon: <ReceiptLongOutlinedIcon fontSize="small" />,
    },
    {
      key: "timeSlots",
      label: t("FasceOrarie"),
      icon: <AccessTimeOutlinedIcon fontSize="small" />,
    },
    {
      key: "EasyInvoice",
      label: t("systemSettings.sectionEasyInvoice", { defaultValue: "EasyInvoice" }),
      icon: <VpnKeyOutlinedIcon fontSize="small" />,
    },
    {
      key: "pms",
      label: "PMS",
      icon: <VpnKeyOutlinedIcon fontSize="small" />,
    },
    {
      key: "smtp",
      label: t("systemSettings.smtp.title", { defaultValue: "SMTP" }),
      icon: <EmailOutlinedIcon fontSize="small" />,
    },
  ].filter((item) => !isConferenceTenant || item.key !== "timeSlots");

  if (isLoading) {
    return (
      <Box className="admin-responsive-page" sx={{ p: { xs: 2, md: 3 }, maxWidth: 920, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="admin-responsive-page" sx={{ p: { xs: 2, md: 3 }, maxWidth: 1180, minWidth: 0 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 2, md: 2.5 },
          borderRadius: 2,
          border: `1px solid ${cardBorder}`,
          background: `linear-gradient(135deg, ${alpha(accentColor, theme.palette.mode === "dark" ? 0.18 : 0.12)} 0%, ${alpha(
            theme.palette.background.paper,
            1
          )} 58%)`,
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accentColor, 0.16),
                color: accentColor,
                flex: "0 0 auto",
              }}
            >
              <SettingsSuggestOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                {t("systemSettings.page")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {t("systemSettings.pageSubtitle", {
                  defaultValue: "Configura le preferenze operative predefinite del sistema.",
                })}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", md: "flex-end" }}>
            <Chip
              label={tenantTypeLabel}
              sx={{ height: 34, fontWeight: 850, bgcolor: alpha(accentColor, 0.14), color: "text.primary" }}
            />
            <Chip
              label={isSaving ? t("systemSettings.saving", { defaultValue: "Salvataggio..." }) : t("systemSettings.ready", { defaultValue: "Pronto" })}
              color={isSaving ? "warning" : "success"}
              variant="outlined"
              sx={{ height: 34, fontWeight: 850 }}
            />
          </Stack>
        </Stack>
      </Paper>

      {loadError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        justifyContent={{ xs: "center", md: "flex-start" }}
        sx={{ mb: 3, flexWrap: "wrap", minWidth: 0 }}
        useFlexGap
      >
        {sectionItems.map((item) => {
          const active = activeSection === item.key;
          return (
            <Button
              key={item.key}
              variant={active ? "contained" : "outlined"}
              onClick={() => setActiveSection(item.key)}
              startIcon={item.icon}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                minHeight: 42,
                px: { xs: 1.25, sm: 1.75 },
                minWidth: 0,
                maxWidth: { xs: "100%", sm: "none" },
                fontWeight: active ? 900 : 750,
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                bgcolor: active ? accentColor : alpha(theme.palette.background.paper, 0.75),
                borderColor: active ? accentColor : cardBorder,
                color: active ? theme.palette.getContrastText(accentColor) : "text.primary",
                "&:hover": {
                  bgcolor: active ? accentColor : alpha(accentColor, 0.08),
                  borderColor: alpha(accentColor, 0.5),
                },
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </Stack>

      {activeSection === "general" && (
        <Paper
          elevation={0}
          sx={sectionCardSx}
        >
          <Box sx={sectionHeaderSx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <SettingsSuggestOutlinedIcon sx={{ color: accentColor }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {t("systemSettings.sectionGeneral", { defaultValue: "Generale" })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.generalHelp", { defaultValue: "Preferenze operative, stampa e colori del planner." })}
                </Typography>
              </Box>
            </Stack>
          </Box>
          <Stack spacing={0}>
            <Box sx={{ px: 2, py: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 112,
                      height: 72,
                      borderRadius: 2,
                      border: `1px solid ${alpha(accentColor, 0.32)}`,
                      bgcolor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.22 : 0.04),
                      display: "grid",
                      placeItems: "center",
                      overflow: "hidden",
                      flex: "0 0 auto",
                    }}
                  >
                    {tenantLogo.base64 ? (
                      <Box
                        component="img"
                        src={tenantLogo.base64}
                        alt={t("systemSettings.logo.alt", { defaultValue: "Logo tenant" })}
                        sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                      />
                    ) : (
                      <ImageOutlinedIcon sx={{ color: alpha(theme.palette.text.secondary, 0.75), fontSize: 34 }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ImageOutlinedIcon fontSize="small" color="warning" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {t("systemSettings.logo.title", { defaultValue: "Logo" })}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                      {tenantLogo.fileName || t("systemSettings.logo.empty", { defaultValue: "Nessun logo caricato" })}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                  <input
                    ref={logoInputRef}
                    hidden
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={handleLogoFileChange}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<ImageOutlinedIcon />}
                    onClick={() => logoInputRef.current?.click()}
                    disabled={isSaving}
                  >
                    {tenantLogo.base64
                      ? t("systemSettings.logo.change", { defaultValue: "Cambia logo" })
                      : t("systemSettings.logo.upload", { defaultValue: "Carica logo" })}
                  </Button>
                  {tenantLogo.base64 && (
                    <Button variant="outlined" color="error" onClick={handleRemoveLogo} disabled={isSaving}>
                      {t("systemSettings.logo.remove", { defaultValue: "Rimuovi" })}
                    </Button>
                  )}
                  <Button variant="contained" onClick={handleSaveLogo} disabled={isSaving}>
                    {isSaving ? t("systemSettings.saving") : t("systemSettings.save")}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {!isConferenceTenant && <Divider />}

            <Box sx={{ px: 2, py: 2, display: isConferenceTenant ? "none" : "block" }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalPrintshopOutlinedIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t("systemSettings.orderPrintFontSize", { defaultValue: "Dimensione testo comanda e preconto" })}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.orderPrintFontSizeHelp", {
                    defaultValue: "Imposta la dimensione degli articoli nelle stampe di comanda, ristampa, differenziale e preconto.",
                  })}
                </Typography>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 260 }, maxWidth: 360 }}>
                  <InputLabel id="order-print-font-size-label">
                    {t("systemSettings.orderPrintFontSize", { defaultValue: "Dimensione testo comanda e preconto" })}
                  </InputLabel>
                  <Select
                    labelId="order-print-font-size-label"
                    value={orderPrintFontSize}
                    label={t("systemSettings.orderPrintFontSize", { defaultValue: "Dimensione testo comanda e preconto" })}
                    onChange={handleOrderPrintFontSizeChange}
                    disabled={isSaving}
                  >
                    <MenuItem value="SMALL">{t("systemSettings.printFontSmall", { defaultValue: "Piccolo" })}</MenuItem>
                    <MenuItem value="NORMAL">{t("systemSettings.printFontNormal", { defaultValue: "Normale" })}</MenuItem>
                    <MenuItem value="LARGE">{t("systemSettings.printFontLarge", { defaultValue: "Grande" })}</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            {!isConferenceTenant && <Divider />}

            <Box sx={{ px: 2, py: 2, display: isConferenceTenant ? "none" : "block" }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocalPrintshopOutlinedIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t("systemSettings.kitchenPrintMenuName")}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t("systemSettings.kitchenPrintMenuNameHelp")}
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={(
                    <Switch
                      checked={showFixedMenuNameOnKitchenPrint}
                      onChange={handleKitchenMenuNameToggle}
                      disabled={isSaving}
                    />
                  )}
                  label={(
                    <Chip
                      size="small"
                      label={showFixedMenuNameOnKitchenPrint
                        ? t("systemSettings.kitchenPrintMenuNameOn")
                        : t("systemSettings.kitchenPrintMenuNameOff")}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                />
              </Stack>
            </Box>

            {!isConferenceTenant && <Divider />}

            <Box sx={{ px: 2, py: 2, display: isConferenceTenant ? "none" : "block" }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ImageOutlinedIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t("systemSettings.showOrderImages")}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t("systemSettings.showOrderImagesHelp", {
                      defaultValue: "Mostra o nascondi le immagini dei prodotti durante la presa ordini.",
                    })}
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={<Switch checked={showOrderImages} onChange={handleToggle} disabled={isSaving} />}
                  label={<Chip size="small" label={showOrderImages ? t("showPic") : t("hidePic")} sx={{ fontWeight: 600 }} />}
                />
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ px: 2, py: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PercentOutlinedIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t("systemSettings.defaultVatRate")}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.defaultVatRateHelp")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }}>
                  <TextField
                    size="small"
                    type="number"
                    label={t("systemSettings.defaultVatRate")}
                    value={defaultVatRate}
                    onChange={(event) => setDefaultVatRate(event.target.value)}
                    disabled={isSaving}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    InputProps={{ endAdornment: <Typography color="text.secondary">%</Typography> }}
                    sx={{ width: { xs: "100%", sm: 220 } }}
                  />
                  <Button variant="contained" onClick={handleSaveDefaultVatRate} disabled={isSaving}>
                    {isSaving ? t("systemSettings.saving") : t("systemSettings.save")}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {!isConferenceTenant && <Divider />}

            <Box sx={{ px: 2, py: 2, display: isConferenceTenant ? "none" : "block" }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <MapOutlinedIcon fontSize="small" color="warning" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t("systemSettings.defaultTableMapView")}
                  </Typography>
                </Stack>

                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 420 }}>
                  <InputLabel id="default-table-map-view-label">{t("systemSettings.defaultTableMapView")}</InputLabel>
                  <Select
                    labelId="default-table-map-view-label"
                    id="default-table-map-view"
                    value={defaultTableMapView}
                    label={t("systemSettings.defaultTableMapView")}
                    onChange={handleDefaultTableMapViewChange}
                    disabled={isSaving}
                  >
                    <MenuItem value="standard">{t("Standard")}</MenuItem>
                    <MenuItem value="dettagliata">{t("Detailed")}</MenuItem>
                  </Select>
                  <FormHelperText>
                    {t("systemSettings.defaultTableMapViewHelp", {
                      defaultValue: "Definisce la vista iniziale quando apri Mappa Tavoli.",
                    })}
                  </FormHelperText>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ px: 2, py: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocalPrintshopOutlinedIcon fontSize="small" sx={{ color: accentColor }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {t("systemSettings.draftPrintersLabel", { defaultValue: "Stampanti preconto" })}
                  </Typography>
                </Stack>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 560 }}>
                  <InputLabel id="draft-printers-label">
                    {t("systemSettings.draftPrintersLabel", { defaultValue: "Stampanti preconto" })}
                  </InputLabel>
                  <Select
                    labelId="draft-printers-label"
                    id="draft-printers"
                    multiple
                    value={draftPrinterTargets}
                    label={t("systemSettings.draftPrintersLabel", { defaultValue: "Stampanti preconto" })}
                    onChange={handleDraftPrinterTargetsChange}
                    disabled={isSaving}
                    renderValue={(selected) =>
                      selected?.length ? selected.join(", ") : t("systemSettings.none", { defaultValue: "Nessuna" })
                    }
                  >
                    {configuredPrinters.map((printerName) => (
                      <MenuItem key={printerName} value={printerName}>
                        <Checkbox checked={draftPrinterTargets.indexOf(printerName) > -1} />
                        <ListItemText primary={printerName} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t("systemSettings.draftPrintersHelp", {
                      defaultValue: "Queste stampanti verranno usate per il preconto (indipendente dagli articoli).",
                    })}
                  </FormHelperText>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 560 }}>
                  <InputLabel id="voucher-printers-label">
                    {t("systemSettings.voucherPrintersLabel", { defaultValue: "Stampanti buoni PMS manuali" })}
                  </InputLabel>
                  <Select
                    labelId="voucher-printers-label"
                    id="voucher-printers"
                    multiple
                    value={voucherPrinterTargets}
                    label={t("systemSettings.voucherPrintersLabel", { defaultValue: "Stampanti buoni PMS manuali" })}
                    onChange={handleVoucherPrinterTargetsChange}
                    disabled={isSaving}
                    renderValue={(selected) =>
                      selected?.length ? selected.join(", ") : t("systemSettings.none", { defaultValue: "Nessuna" })
                    }
                  >
                    {configuredPrinters.map((printerName) => (
                      <MenuItem key={printerName} value={printerName}>
                        <Checkbox checked={voucherPrinterTargets.indexOf(printerName) > -1} />
                        <ListItemText primary={printerName} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t("systemSettings.voucherPrintersHelp", {
                      defaultValue: "Queste stampanti verranno usate per stampare i buoni PMS manuali firmati.",
                    })}
                  </FormHelperText>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 560 }}>
                  <InputLabel id="helite-buono-printers-label">
                    {t("systemSettings.heliteBuonoPrintersLabel", { defaultValue: "Stampanti buoni HElite" })}
                  </InputLabel>
                  <Select
                    labelId="helite-buono-printers-label"
                    id="helite-buono-printers"
                    multiple
                    value={heliteBuonoPrinterTargets}
                    label={t("systemSettings.heliteBuonoPrintersLabel", { defaultValue: "Stampanti buoni HElite" })}
                    onChange={handleHeliteBuonoPrinterTargetsChange}
                    disabled={isSaving}
                    renderValue={(selected) =>
                      selected?.length ? selected.join(", ") : t("systemSettings.none", { defaultValue: "Nessuna" })
                    }
                  >
                    {configuredPrinters.map((printerName) => (
                      <MenuItem key={printerName} value={printerName}>
                        <Checkbox checked={heliteBuonoPrinterTargets.indexOf(printerName) > -1} />
                        <ListItemText primary={printerName} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t("systemSettings.heliteBuonoPrintersHelp", {
                      defaultValue: "Queste stampanti verranno usate per stampare i buoni HElite firmati.",
                    })}
                  </FormHelperText>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 320 }, maxWidth: 560 }}>
                  <InputLabel id="invoice-courtesy-printers-label">
                    {t("systemSettings.invoiceCourtesyPrintersLabel", { defaultValue: "Stampanti copie cortesia fattura" })}
                  </InputLabel>
                  <Select
                    labelId="invoice-courtesy-printers-label"
                    id="invoice-courtesy-printers"
                    multiple
                    value={invoiceCourtesyPrinterTargets}
                    label={t("systemSettings.invoiceCourtesyPrintersLabel", { defaultValue: "Stampanti copie cortesia fattura" })}
                    onChange={handleInvoiceCourtesyPrinterTargetsChange}
                    disabled={isSaving}
                    renderValue={(selected) =>
                      selected?.length ? selected.join(", ") : t("systemSettings.none", { defaultValue: "Nessuna" })
                    }
                  >
                    {configuredPrinters.map((printerName) => (
                      <MenuItem key={printerName} value={printerName}>
                        <Checkbox checked={invoiceCourtesyPrinterTargets.indexOf(printerName) > -1} />
                        <ListItemText primary={printerName} />
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {t("systemSettings.invoiceCourtesyPrintersHelp", {
                      defaultValue: "Queste stampanti verranno usate per stampare copie cortesia compatte delle fatture.",
                    })}
                  </FormHelperText>
                </FormControl>
              </Stack>
            </Box>

            {isConferenceTenant && (
              <>
                <Divider />
                <Box sx={{ px: 2, py: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PaletteOutlinedIcon fontSize="small" sx={{ color: accentColor }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {t("systemSettings.congressAgendaColors", { defaultValue: "Colori agenda sale congressi" })}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {t("systemSettings.congressAgendaColorsHelp", {
                        defaultValue: "Configura i colori usati nel planner per confermati, Check-in, completati e preventivi scaduti.",
                      })}
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        type="color"
                        size="small"
                        label={t("confermata", { defaultValue: "Confermato" })}
                        value={eventConfirmedColor}
                        onChange={(event) => handleEventColorChange("eventConfirmedColor", event.target.value)}
                        disabled={isSaving}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: "100%", sm: 220 } }}
                      />
                      <TextField
                        type="color"
                        size="small"
                        label={t("checkIn", { defaultValue: "Check-in" })}
                        value={eventInProgressColor}
                        onChange={(event) => handleEventColorChange("eventInProgressColor", event.target.value)}
                        disabled={isSaving}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: "100%", sm: 220 } }}
                      />
                      <TextField
                        type="color"
                        size="small"
                        label={t("eventoCompletato", { defaultValue: "Evento completato" })}
                        value={eventCompletedColor}
                        onChange={(event) => handleEventColorChange("eventCompletedColor", event.target.value)}
                        disabled={isSaving}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: "100%", sm: 220 } }}
                      />
                      <TextField
                        type="color"
                        size="small"
                        label={t("preventivoScaduto", { defaultValue: "Preventivo scaduto" })}
                        value={quoteExpiredColor}
                        onChange={(event) => handleEventColorChange("quoteExpiredColor", event.target.value)}
                        disabled={isSaving}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: { xs: "100%", sm: 220 } }}
                      />
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        </Paper>
      )}

      {!isConferenceTenant && activeSection === "timeSlots" && (
        <Paper
          elevation={0}
          sx={sectionCardSx}
        >
          <Box sx={sectionHeaderSx}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeOutlinedIcon fontSize="small" sx={{ color: accentColor }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t("FasceOrarie")}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t("InserisciFasceOrarie")}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.25, sm: 2, md: 2.5 }, minWidth: 0, maxWidth: "100%" }}>

            <Stack spacing={2} alignItems="center" sx={{ mt: 2, minWidth: 0 }}>
              {timeSlots.map((slot, index) => (
                <Paper
                  key={slot.id}
                  variant="outlined"
                  sx={{
                    p: { xs: 1.25, sm: 2 },
                    borderRadius: 2,
                    borderColor: alpha(accentColor, 0.22),
                    bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.28 : 0.45),
                    width: "100%",
                    mx: "auto",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Stack spacing={2} sx={{ minWidth: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Chip
                          size="small"
                          label={index + 1}
                          sx={{ fontWeight: 900, bgcolor: alpha(accentColor, 0.16), color: "text.primary", flexShrink: 0 }}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, overflowWrap: "anywhere" }}>
                          {t("Fascia")} {index + 1}
                        </Typography>
                      </Stack>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveTimeSlot(slot.id)}
                        disabled={isSaving || timeSlots.length <= 1}
                        aria-label={t("Rimuovi")}
                      >
                        <DeleteOutlineOutlinedIcon />
                      </IconButton>
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "minmax(0, 1fr)",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "minmax(0, 5fr) minmax(0, 3fr) minmax(0, 3fr)",
                        },
                        gap: { xs: 1.5, sm: 2 },
                        justifyItems: "center",
                        width: "100%",
                        minWidth: 0,
                      }}
                    >
                      <Box sx={{ minWidth: 0, width: "100%", maxWidth: { xs: 276, sm: "100%" } }}>
                        <TextField
                          fullWidth
                          size="small"
                          label={t("NomeFascia")}
                          value={translateTimeSlotLabel(slot.label)}
                          onChange={(e) => handleUpdateTimeSlot(slot.id, "label", e.target.value)}
                          disabled={isSaving}
                          placeholder={t("Breakfast")}
                          sx={{ minWidth: 0, width: "100%" }}
                          inputProps={{ style: { minWidth: 0 } }}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0, width: "100%", maxWidth: { xs: 276, sm: "100%" } }}>
                        <TimePicker24Field
                          label={t("Da")}
                          value={parseTimePickerValue(slot.start)}
                          onChange={(value) => handleUpdateTimeSlot(slot.id, "start", formatTimePickerValue(value))}
                          ampm={false}
                          format="HH:mm"
                          disabled={isSaving}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              sx={{ minWidth: 0, width: "100%" }}
                              inputProps={{
                                ...params.inputProps,
                                readOnly: true,
                                autoComplete: "off",
                                "aria-autocomplete": "none",
                              }}
                              InputProps={{
                                ...params.InputProps,
                                autoComplete: "off",
                              }}
                            />
                          )}
                        />
                      </Box>
                      <Box sx={{ minWidth: 0, width: "100%", maxWidth: { xs: 276, sm: "100%" } }}>
                        <TimePicker24Field
                          label={t("A")}
                          value={parseTimePickerValue(slot.end)}
                          onChange={(value) => handleUpdateTimeSlot(slot.id, "end", formatTimePickerValue(value))}
                          ampm={false}
                          format="HH:mm"
                          disabled={isSaving}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              sx={{ minWidth: 0, width: "100%" }}
                              inputProps={{
                                ...params.inputProps,
                                readOnly: true,
                                autoComplete: "off",
                                "aria-autocomplete": "none",
                              }}
                              InputProps={{
                                ...params.InputProps,
                                autoComplete: "off",
                              }}
                            />
                          )}
                        />
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      {t("EsempioFasciaOraria")}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              <Button variant="outlined" startIcon={<AddOutlinedIcon />} onClick={handleAddTimeSlot} disabled={isSaving}>
                {t("AggiungiFascia")}
              </Button>
              <Button variant="contained" onClick={handleSaveTimeSlots} disabled={isSaving}>
                {isSaving ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} />
                    <span>{t("Salvataggio")}</span>
                  </Stack>
                ) : (
                  t("SalvaFasceOrarie")
                )}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      {activeSection === "billing" && (
        <Paper
          elevation={0}
          sx={sectionCardSx}
        >
          <Box sx={sectionHeaderSx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ReceiptLongOutlinedIcon sx={{ color: accentColor }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {t("systemSettings.billingOwnerData")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.billingOwnerDataHelp", { defaultValue: "Dati fiscali usati nei documenti e nelle integrazioni." })}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Grid container spacing={2}>
              {[
                ["ownerName", "ownerName"],
                ["ownerSurname", "ownerSurname"],
                ["ownerTaxCode", "ownerTaxCode"],
                ["ownerVat", "ownerVat"],
                ["ownerAddress", "ownerAddress"],
                ["ownerPostalCode", "ownerPostalCode"],
                ["ownerCity", "ownerCity"],
                ["ownerProvince", "ownerProvince"],
                ["ownerEmail", "ownerEmail"],
                ["ownerPhone", "ownerPhone"],
              ].map(([field, i18nKey]) => (
                <Grid item xs={12} sm={6} key={field}>
                  <Typography variant="body1" color="text.secondary">
                    {t(`systemSettings.${i18nKey}`)}
                  </Typography>
                  {isEdit ? (
                    <TextField
                      fullWidth
                      value={billingData[field]}
                      onChange={(e) => handleBillingChange(field, e.target.value)}
                      disabled={isSaving}
                      size="small"
                      type={field === "ownerEmail" ? "email" : "text"}
                    />
                  ) : (
                    <Typography variant="body1" color="text.primary">
                      {billingData[field] || "-"}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderTop: `1px solid ${cardBorder}` }}>
            {isEdit ? (
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => setIsEdit(false)} disabled={isSaving}>
                  {t("systemSettings.cancel", { defaultValue: "Annulla" })}
                </Button>
                <Button variant="contained" onClick={handleSaveBillingData} disabled={isSaving}>
                  {isSaving ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} />
                      <span>{t("systemSettings.saving", { defaultValue: "Salvataggio..." })}</span>
                    </Stack>
                  ) : (
                    t("systemSettings.save", { defaultValue: "Salva" })
                  )}
                </Button>
              </Stack>
            ) : (
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setIsEdit(true)}>
                {t("systemSettings.edit", { defaultValue: "Modifica" })}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {activeSection === "EasyInvoice" && (
        <Paper
          elevation={0}
          sx={sectionCardSx}
        >
          <Box sx={sectionHeaderSx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <VpnKeyOutlinedIcon sx={{ color: accentColor }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {t("systemSettings.easyInvoiceTitle", { defaultValue: "Credenziali di accesso" })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.easyInvoiceHelp", { defaultValue: "Credenziali per il collegamento al servizio EasyInvoice." })}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Grid container spacing={2}>
              {[
                ["user", "user"],
                ["client_id", "client_id"],
                ["secret", "secret"],
              ].map(([field, i18nKey]) => (
                <Grid item xs={12} md={4} key={field}>
                  <Typography variant="body1" color="text.secondary">
                    {t(`systemSettings.${i18nKey}`)}
                  </Typography>
                  {isEdit ? (
                    <TextField
                      fullWidth
                      value={easyInvoiceLogin[field]}
                      onChange={(e) => handleEasyInvoiceChange(field, e.target.value)}
                      disabled={isSaving}
                      size="small"
                      type="text"
                    />
                  ) : (
                    <Typography variant="body1" color="text.primary">
                      {easyInvoiceLogin[field] || "-"}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderTop: `1px solid ${cardBorder}` }}>
            {isEdit ? (
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => setIsEdit(false)} disabled={isSaving}>
                  {t("systemSettings.cancel", { defaultValue: "Annulla" })}
                </Button>
                <Button variant="contained" onClick={handleSaveEasyInvoiceLogin} disabled={isSaving}>
                  {isSaving ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} />
                      <span>{t("systemSettings.saving", { defaultValue: "Salvataggio..." })}</span>
                    </Stack>
                  ) : (
                    t("systemSettings.save", { defaultValue: "Salva" })
                  )}
                </Button>
              </Stack>
            ) : (
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setIsEdit(true)}>
                {t("systemSettings.edit", { defaultValue: "Modifica" })}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {activeSection === "pms" && (
        <Paper
          elevation={0}
          sx={sectionCardSx}
        >
          <Box sx={sectionHeaderSx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <VpnKeyOutlinedIcon sx={{ color: accentColor }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {t("systemSettings.pms.title", { defaultValue: "PMS HElite" })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.pms.help", {
                    defaultValue: "Credenziali per il collegamento al PMS HElite.",
                  })}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Grid container spacing={2}>
              {[
                ["heliteUrl", "URL HElite", "url", 12],
                ["heliteClientId", "client_id", "text", 12],
                ["heliteUsername", "username", "text", 6],
                ["helitePassword", "password", "password", 6],
                ["heliteHotelCode", "hotelcode", "text", 6],
              ].map(([field, label, type, sm]) => (
                <Grid item xs={12} sm={sm} key={field}>
                  <Typography variant="body1" color="text.secondary">
                    {label}
                  </Typography>
                  {isEdit ? (
                    <TextField
                      fullWidth
                      value={heliteData[field]}
                      onChange={(e) => handleHeliteChange(field, e.target.value)}
                      disabled={isSaving}
                      size="small"
                      type={type}
                      placeholder={
                        field === "helitePassword" && heliteData.hasHelitePassword
                          ? t("systemSettings.pms.passwordPlaceholder", {
                            defaultValue: "Password salvata: lascia vuoto per mantenerla.",
                          })
                          : ""
                      }
                    />
                  ) : (
                    <Typography variant="body1" color="text.primary" sx={{ wordBreak: "break-word" }}>
                      {field === "helitePassword"
                        ? ((heliteData.helitePassword || heliteData.hasHelitePassword) ? "••••••••" : "-")
                        : (heliteData[field] || "-")}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>

          </Box>

          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2.5, borderTop: `1px solid ${cardBorder}` }}>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
                spacing={1.5}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {t("systemSettings.pms.mapping.title", { defaultValue: "Mapping servizi HElite" })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("systemSettings.pms.mapping.help", {
                      defaultValue: "Draft frontend per associare vendita, categorie, sottocategorie o prodotti ai codici struttura/reparto/servizio HElite.",
                    })}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      loadHeliteMappingCatalog();
                      loadHeliteMappingRooms();
                      loadHeliteServiceMappings();
                      loadHeliteChargeCodes();
                    }}
                    disabled={heliteMappingCatalogLoading || heliteMappingRoomsLoading || heliteMappingLoading || heliteMappingSaving || heliteChargeCodesLoading}
                  >
                    {heliteMappingCatalogLoading || heliteMappingRoomsLoading || heliteMappingLoading || heliteChargeCodesLoading
                      ? t("systemSettings.pms.mapping.loadingCatalog", { defaultValue: "Caricamento..." })
                      : t("systemSettings.pms.mapping.reloadCatalog", { defaultValue: "Ricarica catalogo" })}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={saveHeliteMappingRules}
                    disabled={!heliteMappingDirty || heliteMappingSaving || heliteMappingLoading}
                  >
                    {heliteMappingSaving
                      ? t("systemSettings.saving", { defaultValue: "Salvataggio..." })
                      : t("systemSettings.pms.mapping.save", { defaultValue: "Salva mapping" })}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={resetHeliteMappingDraft}
                    disabled={!heliteMappingDirty || heliteMappingSaving}
                  >
                    {t("systemSettings.pms.mapping.reset", { defaultValue: "Annulla modifiche" })}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={clearHeliteMappings}
                    disabled={heliteMappingRules.length === 0 || heliteMappingSaving}
                  >
                    {t("systemSettings.pms.mapping.clear", { defaultValue: "Svuota mapping" })}
                  </Button>
                </Stack>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={5}>
                  <Box
                    sx={{
                      border: `1px solid ${cardBorder}`,
                      borderRadius: 1.5,
                      p: 1.5,
                      height: "100%",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      {t("systemSettings.pms.mapping.targets", { defaultValue: "Trascina o tocca un target" })}
                    </Typography>
                    <Stack spacing={1.25}>
                      {["DEFAULT", "CATEGORY", "SUBCATEGORY", "PRODUCT"].map((scopeType) => {
                        const targets = heliteMappingTargets.filter((target) => target.scopeType === scopeType);
                        return (
                          <Box key={scopeType}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                              {HELITE_MAPPING_SCOPE_LABELS[scopeType]}
                              {scopeType !== "DEFAULT" ? ` (${targets.length})` : ""}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{
                                mt: 0.75,
                                maxHeight: scopeType === "PRODUCT" ? 150 : 96,
                                overflowY: "auto",
                                pr: 0.5,
                              }}
                            >
                              {targets.map((target) => {
                                const key = mappingTargetKey(target);
                                const mappedRules = heliteMappingRulesByTarget.get(key) || [];
                                const mapped = mappedRules.length > 0;
                                const inactive = mappedRules.some(isHeliteMappingInactive);
                                const selected = selectedMappingTargetKey === key;
                                const mappedDestinations = mappedRules
                                  .map((rule) => `${rule.structCode}/${rule.dep}/${rule.service} (${getHeliteRuleContextLabel(rule)})`)
                                  .join(", ");
                                return (
                                  <Chip
                                    key={key}
                                    draggable
                                    onClick={() => handleHeliteMappingTargetSelect(target)}
                                    onDragStart={(event) => handleHeliteMappingDragStart(event, target)}
                                    onDragEnd={() => setDraggedMappingTargetKey("")}
                                    label={`${target.label || key} - IVA ${formatVatPercent(target.vatRate)}%${inactive ? " - NON ATTIVO" : ""}`}
                                    size="small"
                                    color={inactive ? "error" : (mapped ? "warning" : "default")}
                                    variant={mapped || selected ? "filled" : "outlined"}
                                    title={inactive
                                      ? `${target.helper || target.label || key} - NON ATTIVO: ${mappedDestinations}`
                                      : [target.helper || target.label, mappedDestinations].filter(Boolean).join(" -> ")}
                                    sx={{
                                      cursor: "grab",
                                      maxWidth: 220,
                                      opacity: draggedMappingTargetKey === key ? 0.55 : 1,
                                      touchAction: "manipulation",
                                      ...(selected ? {
                                        outline: `2px solid ${accentColor}`,
                                        outlineOffset: 1,
                                      } : {}),
                                      "& .MuiChip-label": {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      },
                                    }}
                                  />
                                );
                              })}
                              {targets.length === 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  {heliteMappingCatalogLoading
                                    ? t("systemSettings.pms.mapping.loadingCatalog", { defaultValue: "Caricamento..." })
                                    : t("systemSettings.pms.mapping.noTargets", { defaultValue: "Nessun elemento." })}
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} lg={7}>
                  <Box
                    sx={{
                      border: `1px solid ${cardBorder}`,
                      borderRadius: 1.5,
                      p: 1.5,
                      height: "100%",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      {t("systemSettings.pms.mapping.destinations", { defaultValue: "Rilascia o tocca un servizio HElite" })}
                    </Typography>
                    {heliteChargeCodesLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">
                          {t("systemSettings.pms.loadingCodes", { defaultValue: "Caricamento..." })}
                        </Typography>
                      </Stack>
                    ) : heliteChargeCodes.length === 0 ? (
                      <Alert severity="warning">
                        {t("systemSettings.pms.mapping.loadCodesFirst", {
                          defaultValue: "Carica prima i codici HElite per vedere strutture, reparti e servizi disponibili.",
                        })}
                      </Alert>
                    ) : (
                      <Stack spacing={1.25} sx={{ maxHeight: 430, overflowY: "auto", pr: 0.5 }}>
                        {heliteChargeCodes.map((structure) => (
                          <Box key={structure.code}>
                            <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                              {structure.code} - {structure.name || structure.code}
                            </Typography>
                            <Stack spacing={0.75}>
                              {(structure.debits || []).map((debit) => {
                                const key = debitKey(structure.code, debit);
                                const assigned = heliteMappingsByDebit.get(key) || [];
                                return (
                                  <Box
                                    key={key}
                                    role="button"
                                    tabIndex={selectedMappingTarget ? 0 : -1}
                                    onDragOver={(event) => {
                                      event.preventDefault();
                                      event.dataTransfer.dropEffect = "copy";
                                    }}
                                    onDrop={(event) => handleHeliteMappingDrop(event, structure, debit)}
                                    onClick={() => handleHeliteMappingServiceClick(structure, debit)}
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        handleHeliteMappingServiceClick(structure, debit);
                                      }
                                    }}
                                    sx={{
                                      border: `1px dashed ${assigned.length ? alpha(accentColor, 0.65) : cardBorder}`,
                                      borderRadius: 1,
                                      p: 1,
                                      background: assigned.length
                                        ? alpha(accentColor, theme.palette.mode === "dark" ? 0.14 : 0.08)
                                        : alpha(theme.palette.action.hover, 0.35),
                                      cursor: selectedMappingTarget ? "pointer" : "default",
                                      touchAction: "manipulation",
                                      ...(selectedMappingTarget && ratesMatch(selectedMappingTarget.vatRate, debit.vatPercent) ? {
                                        borderColor: accentColor,
                                        boxShadow: `0 0 0 1px ${alpha(accentColor, 0.45)}`,
                                      } : {}),
                                    }}
                                  >
                                    <Stack
                                      direction={{ xs: "column", sm: "row" }}
                                      justifyContent="space-between"
                                      alignItems={{ xs: "flex-start", sm: "center" }}
                                      spacing={1}
                                    >
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                          {debit.dep} - {debit.depName || debit.dep}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {debit.service} - {debit.serviceName || debit.service}
                                          {" | "}
                                          IVA {formatVatPercent(debit.vatPercent)}%
                                        </Typography>
                                      </Box>
                                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                                        {assigned.length === 0 ? (
                                          <Typography variant="caption" color="text.secondary">
                                            {selectedMappingTarget
                                              ? t("systemSettings.pms.mapping.tapHere", { defaultValue: "Tocca qui" })
                                              : t("systemSettings.pms.mapping.dropHere", { defaultValue: "Drop qui" })}
                                          </Typography>
                                        ) : assigned.map((rule) => (
                                          <Chip
                                            key={mappingRuleKey(rule)}
                                            size="small"
                                            color="warning"
                                            label={`${HELITE_MAPPING_SCOPE_LABELS[rule.scopeType] || rule.scopeType}: ${getHeliteRuleLabel(rule)} - IVA ${formatVatPercent(rule.vatPercent)}% - ${getHeliteRuleContextLabel(rule)}`}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openHeliteMappingRuleEditor(rule);
                                            }}
                                            onDelete={(event) => {
                                              event.stopPropagation();
                                              removeHeliteMapping(rule);
                                            }}
                                          />
                                        ))}
                                      </Stack>
                                    </Stack>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Grid>
              </Grid>

              <Dialog
                open={heliteMappingDialog.open}
                onClose={closeHeliteMappingDialog}
                fullWidth
                maxWidth="sm"
              >
                <DialogTitle>
                  {t("systemSettings.pms.mapping.scopeDialogTitle", { defaultValue: "Dettagli mapping HElite" })}
                </DialogTitle>
                <DialogContent>
                  <Stack spacing={2} sx={{ pt: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {heliteMappingDialog.target?.label || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[
                          heliteMappingDialog.structure?.code,
                          heliteMappingDialog.debit?.dep,
                          heliteMappingDialog.debit?.service,
                        ].filter(Boolean).join(" / ")}
                        {heliteMappingDialog.debit?.vatPercent !== undefined
                          ? ` - IVA ${formatVatPercent(heliteMappingDialog.debit.vatPercent)}%`
                          : ""}
                      </Typography>
                    </Box>

                    <FormControl
                      fullWidth
                      size="small"
                      disabled={heliteMappingRoomsLoading}
                    >
                      <InputLabel id="helite-mapping-rooms-label" shrink>
                        {t("systemSettings.pms.mapping.rooms", { defaultValue: "Sale" })}
                      </InputLabel>
                      <Select
                        labelId="helite-mapping-rooms-label"
                        multiple
                        displayEmpty
                        notched
                        value={heliteMappingDialog.roomIds}
                        label={t("systemSettings.pms.mapping.rooms", { defaultValue: "Sale" })}
                        onChange={(event) => {
                          setHeliteMappingDialog((prev) => ({
                            ...prev,
                            roomIds: normalizeIdArray(event.target.value),
                          }));
                        }}
                        renderValue={(selected) => {
                          const roomIds = normalizeIdArray(selected);
                          return roomIds.length
                            ? roomIds.map((roomId) => heliteMappingRoomById.get(roomId)?.name || `Sala ${roomId}`).join(", ")
                            : t("systemSettings.pms.mapping.allRooms", { defaultValue: "Tutte le sale" });
                        }}
                      >
                        {heliteMappingRooms.map((room) => (
                          <MenuItem key={room.id} value={Number(room.id)}>
                            <Checkbox checked={heliteMappingDialog.roomIds.includes(Number(room.id))} />
                            <ListItemText primary={room.name || `Sala ${room.id}`} />
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {heliteMappingRoomsLoading
                          ? t("systemSettings.pms.mapping.loadingRooms", { defaultValue: "Caricamento sale..." })
                          : heliteMappingRooms.length === 0
                            ? t("systemSettings.pms.mapping.noRooms", { defaultValue: "Nessuna sala configurata." })
                            : t("systemSettings.pms.mapping.roomsHelp", { defaultValue: "Lascia vuoto per rendere il mapping valido anche per vendita al banco e tutte le sale." })}
                      </FormHelperText>
                    </FormControl>

                    <FormControl
                      fullWidth
                      size="small"
                    >
                      <InputLabel id="helite-mapping-timeslots-label" shrink>
                        {t("systemSettings.pms.mapping.timeSlots", { defaultValue: "Fasce orarie" })}
                      </InputLabel>
                      <Select
                        labelId="helite-mapping-timeslots-label"
                        multiple
                        displayEmpty
                        notched
                        value={heliteMappingDialog.timeSlotNames}
                        label={t("systemSettings.pms.mapping.timeSlots", { defaultValue: "Fasce orarie" })}
                        onChange={(event) => {
                          setHeliteMappingDialog((prev) => ({
                            ...prev,
                            timeSlotNames: normalizeNameArray(event.target.value),
                          }));
                        }}
                        renderValue={(selected) => {
                          const slotNames = normalizeNameArray(selected);
                          return slotNames.length
                            ? slotNames.map(translateTimeSlotLabel).join(", ")
                            : t("systemSettings.pms.mapping.allTimeSlots", { defaultValue: "Tutte le fasce" });
                        }}
                      >
                        {configuredTimeSlotNames.map((name) => (
                          <MenuItem key={name} value={name}>
                            <Checkbox checked={heliteMappingDialog.timeSlotNames.includes(name)} />
                            <ListItemText primary={translateTimeSlotLabel(name)} secondary={name} />
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        {configuredTimeSlotNames.length === 0
                          ? t("systemSettings.pms.mapping.noTimeSlots", { defaultValue: "Nessuna fascia oraria configurata." })
                          : t("systemSettings.pms.mapping.timeSlotsHelp", { defaultValue: "Lascia vuoto per applicare il mapping a qualsiasi fascia oraria." })}
                      </FormHelperText>
                    </FormControl>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeHeliteMappingDialog}>
                    {t("systemSettings.cancel", { defaultValue: "Annulla" })}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={confirmHeliteMappingDialog}
                    disabled={
                      heliteMappingRoomsLoading
                    }
                  >
                    {t("systemSettings.pms.mapping.confirmScope", { defaultValue: "Conferma mapping" })}
                  </Button>
                </DialogActions>
              </Dialog>

              {inactiveHeliteMappingRules.length > 0 && (
                <Alert severity="error">
                  <Stack spacing={0.75}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {t("systemSettings.pms.mapping.inactiveTitle", { defaultValue: "Mapping HElite non attivi" })}
                    </Typography>
                    {inactiveHeliteMappingRules.map((rule) => (
                      <Typography key={mappingRuleKey(rule)} variant="caption" sx={{ display: "block" }}>
                        {`${HELITE_MAPPING_SCOPE_LABELS[rule.scopeType] || rule.scopeType}: ${getHeliteRuleLabel(rule)} - ${getHeliteRuleContextLabel(rule)} - NON ATTIVO -> ${rule.structCode}/${rule.dep}/${rule.service}`}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              {heliteMappingRules.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                    {t("systemSettings.pms.mapping.currentRules", { defaultValue: "Regole configurate" })}
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {heliteMappingRules.map((rule) => {
                      const inactive = isHeliteMappingInactive(rule);
                      return (
                        <Chip
                          key={mappingRuleKey(rule)}
                          size="small"
                          variant="outlined"
                          color={inactive ? "error" : "warning"}
                          label={`${HELITE_MAPPING_SCOPE_LABELS[rule.scopeType] || rule.scopeType}: ${getHeliteRuleLabel(rule)} - IVA ${formatVatPercent(rule.vatPercent)}% - ${getHeliteRuleContextLabel(rule)} -> ${rule.structCode}/${rule.dep}/${rule.service}${inactive ? " - NON ATTIVO" : ""}`}
                          onClick={() => openHeliteMappingRuleEditor(rule)}
                          onDelete={() => removeHeliteMapping(rule)}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Box>

          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderTop: `1px solid ${cardBorder}` }}>
            {isEdit ? (
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => setIsEdit(false)} disabled={isSaving}>
                  {t("systemSettings.cancel", { defaultValue: "Annulla" })}
                </Button>
                <Button variant="contained" onClick={handleSaveHeliteData} disabled={isSaving}>
                  {isSaving ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} />
                      <span>{t("systemSettings.saving", { defaultValue: "Salvataggio..." })}</span>
                    </Stack>
                  ) : (
                    t("systemSettings.save", { defaultValue: "Salva" })
                  )}
                </Button>
              </Stack>
            ) : (
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setIsEdit(true)}>
                {t("systemSettings.edit", { defaultValue: "Modifica" })}
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {activeSection === "smtp" && (
        <Paper
          elevation={0}
          sx={sectionCardSx}
        >
          <Box sx={sectionHeaderSx}>
            <Stack direction="row" spacing={1} alignItems="center">
              <EmailOutlinedIcon sx={{ color: accentColor }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                  {t("systemSettings.smtp.title", { defaultValue: "Configurazione SMTP" })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("systemSettings.smtp.help", {
                    defaultValue: "Server email usato dal tenant per inviare preventivi, riepiloghi e comunicazioni.",
                  })}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Grid container spacing={2}>
              {[
                ["indirizzoEmail", "INDIRIZZO_EMAIL", "email"],
                ["userLogin", "USER_LOGIN", "text"],
                ["passwordLogin", "PASSWORD_LOGIN", "password"],
                ["serverSmtp", "SERVER_SMTP", "text"],
                ["portaSmtp", "PORTA_SMTP", "number"],
              ].map(([field, label, type]) => (
                <Grid item xs={12} sm={field === "portaSmtp" ? 4 : 6} key={field}>
                  <Typography variant="body1" color="text.secondary">
                    {label}
                  </Typography>
                  {isEdit ? (
                    <TextField
                      fullWidth
                      value={smtpData[field]}
                      onChange={(e) => handleSmtpChange(field, e.target.value)}
                      disabled={isSaving}
                      size="small"
                      type={type}
                      inputProps={field === "portaSmtp" ? { min: 1, max: 65535 } : undefined}
                      placeholder={
                        field === "passwordLogin" && smtpData.hasSmtpPassword
                          ? t("systemSettings.smtp.passwordPlaceholder", {
                            defaultValue: "Password salvata: lascia vuoto per mantenerla.",
                          })
                          : ""
                      }
                    />
                  ) : (
                    <Typography variant="body1" color="text.primary">
                      {field === "passwordLogin"
                        ? (smtpData.hasSmtpPassword ? "••••••••" : "-")
                        : (smtpData[field] || "-")}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderTop: `1px solid ${cardBorder}` }}>
            {isEdit ? (
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" onClick={() => setIsEdit(false)} disabled={isSaving}>
                  {t("systemSettings.cancel", { defaultValue: "Annulla" })}
                </Button>
                <Button variant="contained" onClick={handleSaveSmtpData} disabled={isSaving}>
                  {isSaving ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={20} />
                      <span>{t("systemSettings.saving", { defaultValue: "Salvataggio..." })}</span>
                    </Stack>
                  ) : (
                    t("systemSettings.save", { defaultValue: "Salva" })
                  )}
                </Button>
              </Stack>
            ) : (
              <Button variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => setIsEdit(true)}>
                {t("systemSettings.edit", { defaultValue: "Modifica" })}
              </Button>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default SystemSettingsPage;
